import React, { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import { alertErrorMessage, alertSuccessMessage } from "../../../customComponents/CustomAlertMessage";
import FuturesOrderRejectionModal from "./FuturesOrderRejectionModal";
import FuturesRiskWarningModal, {
  FUTURES_RISK_WARNING_STORAGE_KEY,
} from "./FuturesRiskWarningModal";
import {
    FUTURES_LEVERAGE_REJECTION_MSG,
    computeFuturesOrderMargin,
    computeMaxOpenNotional,
    computeMaxOpenQtyBtc,
    resolveTakerFeeRate,
    getMaxNotionalAtLeverage,
    isFuturesLeverageRejectionMessage,
} from "./futuresLeverageHelpers";
import {
    decNum as futuresDecNum,
    findFuturesContractInPairs,
    resolveIncrementSize,
    snapAndCapCloseQty,
    snapToIncrementInput,
} from "./futuresIncrementHelpers";
import {
    convertFuturesAmountToBaseQty,
    denomFromFuturesContractUnitId,
    isFuturesValueUnit,
    normalizeFuturesContractUnitId,
    resolveFuturesAmountIsQuote,
    resolveFuturesPriceForQtyConversion,
} from "./futuresOrderSizing";
import { amountLimitLabel, effectiveOrderMinimum } from "../TradePage/spotQtyPrecision";
import {
    formatFuturesApiError,
    futuresErrGeneric,
    futuresErrInvalidPosition,
    futuresErrIocFokReduceOnly,
    futuresErrLeverageMax,
    futuresErrLeverageRange,
    futuresErrOrderType,
    futuresErrReduceOnlyConflict,
    futuresErrSelectPair,
} from "./futuresOrderMessages";
import { FUTURES_WALLET_HISTORY_DEFAULT_FILTERS } from "./futuresWalletHistoryHelpers";
import "../TradePage/trade_new.css";
import "./margin.css";
import "./futures_trade_extras.css";
import { FuturesTradeTransferProvider } from "./FuturesTradeTransferContext";
import { resolvePublicUploadUrl } from "../../../api/apiConfig/apiConfig";
import AuthService from "../../../api/services/AuthService";
import LoaderHelper from "../../../customComponents/Loading/LoaderHelper";
import { ProfileContext } from "../../../context/ProfileProvider";
import { usePlatformStatus } from "../../../context/PlatformStatusProvider";
import { SocketContext } from "../../../customComponents/SocketContext";
import { Helmet } from "react-helmet-async";
import { DISABLE_ALL_NETWORK } from "../../../networkConfig";
import TradePairsSection from "./components/TradePairsSection";
import TradeCenterSection from "./components/TradeCenterSection";
import TradeHistorySection from "./components/TradeHistorySection";
import {
    dedupeSpotHistoryById,
    normalizeSpotOrdersHistoryItemsForTrade,
    normalizeSpotTradeFillsItemsForTrade,
} from "./tradePageSpotHistoryNormalization";
import {
    MARGIN_CLASSIC_SPOT_FORM_KINDS,
    MARGIN_SPOT_API_ORDER_KINDS,
    marginOrderUsesStopTriggerField,
    marginShowAmtDenomSelect,
} from "./marginConditionalOrderMenu";

const DEFAULT_ORDER_BOOK_AGG_OPTIONS = [0.1, 0.5, 1, 10, 100];
const ORDER_BOOK_BOTH_DEPTH = 10;

/** Unique id per place click — backend dedups without this (60s fingerprint). */
function newFuturesClientOrderId() {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
        return `web-${crypto.randomUUID()}`;
    }
    return `web-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/** Available label — prefer effective_available (margin usable for new orders). */
function futuresBalanceForDisplay(balance) {
    if (!balance) return null;
    if (balance.effective_available != null && balance.effective_available !== "") {
        const eff = Number(balance.effective_available);
        if (Number.isFinite(eff)) return eff;
    }
    if (balance.available_balance != null && balance.available_balance !== "") {
        const avail = Number(balance.available_balance);
        if (Number.isFinite(avail)) return avail;
    }
    return null;
}

/** Socket payloads may use order_id / position_id instead of _id. */
function normalizeFuturesSocketOrder(raw) {
    if (!raw || typeof raw !== "object") return raw;
    const id = raw._id ?? raw.order_id ?? raw.id;
    return id != null ? { ...raw, _id: id } : raw;
}

function normalizeFuturesSocketPosition(raw) {
    if (!raw || typeof raw !== "object") return raw;
    const id = raw._id ?? raw.position_id ?? raw.id;
    return id != null ? { ...raw, _id: id } : raw;
}

function normalizeFuturesSocketOrders(list) {
    return (Array.isArray(list) ? list : []).map(normalizeFuturesSocketOrder).filter(Boolean);
}

function normalizeFuturesSocketPositions(list) {
    return (Array.isArray(list) ? list : []).map(normalizeFuturesSocketPosition).filter(Boolean);
}

/** Map socket `balance` blob → account row shape for FuturesAccountPanel. */
function futuresAccountsFromSocketBalance(balance, positions = []) {
    if (!balance || typeof balance !== "object") return [];
    const marginAsset = balance.margin_asset || "USDT";
    const isolatedReserved = parseFloat(balance.isolated_reserved);
    const hasIsolatedPos = (positions || []).some(
        (p) => String(p?.margin_mode ?? "").toUpperCase() === "ISOLATED"
    );
    const marginMode =
        hasIsolatedPos || (Number.isFinite(isolatedReserved) && isolatedReserved > 0)
            ? "ISOLATED"
            : "CROSS";
    return [{
        margin_asset: marginAsset,
        margin_mode: marginMode,
        available_balance: balance.available_balance,
        locked_balance: balance.locked_balance,
        isolated_reserved: balance.isolated_reserved,
    }];
}

function applyFuturesSocketBalance(balance, setters) {
    if (!balance) return;
    const displayBal = futuresBalanceForDisplay(balance);
    setters.setBuyCoinBal(displayBal);
    setters.setSellCoinBal(displayBal);
    if (balance.effective_available != null) {
        setters.setFuturesEffectiveAvailable(Number(balance.effective_available));
    }
    setters.setFuturesSocketBalance(balance);
}

function tickDecimals(tickSize) {
    const s = String(tickSize ?? "");
    if (!s || Number(s) >= 1) return 0;
    const dot = s.indexOf(".");
    return dot === -1 ? 0 : s.length - dot - 1;
}

function formatContractPrice(price, tickSize) {
    const n = parseFloat(price);
    if (!Number.isFinite(n)) return price ?? "—";
    return n.toFixed(tickDecimals(tickSize));
}

/** Spot history tab: API page size / polling / infinite scroll (trade page panels). */
const SPOT_HIST_PAGE_SIZE = 20;
const SPOT_HIST_SCROLL_TOP_EPS = 6;
const SPOT_HIST_LOAD_MORE_PX = 96;
function getOrderBookAggOptionsForPair(tickSize) {
    const tick = Number(tickSize);
    if (!Number.isFinite(tick) || tick <= 0) {
        return DEFAULT_ORDER_BOOK_AGG_OPTIONS.slice();
    }
    const mults = [1, 10, 100, 1000, 10000];
    const out = [];
    for (const m of mults) {
        const v = tick * m;
        if (!Number.isFinite(v) || v <= 0) continue;
        out.push(parseFloat(v.toPrecision(12)));
    }
    const unique = Array.from(new Set(out)).sort((a, b) => a - b);
    return unique.length ? unique : DEFAULT_ORDER_BOOK_AGG_OPTIONS.slice();
}

function formatOrderBookNotionalShort(notional) {
    const n = Number(notional);
    if (!Number.isFinite(n)) return "0";
    const abs = Math.abs(n);
    if (abs >= 1e9) return `${(n / 1e9).toFixed(2)}B`;
    if (abs >= 1e6) return `${(n / 1e6).toFixed(2)}M`;
    if (abs >= 1e3) return `${(n / 1e3).toFixed(2)}K`;
    if (abs >= 1) return n.toFixed(2);
    return n.toFixed(4);
}

function roundPriceToAgg(price, agg) {
    const n = Number(price);
    if (!Number.isFinite(n) || !agg) return n;
    return Math.round(n / agg) * agg;
}

function aggregateOrderBookRows(orders, agg) {
    if (!orders?.length) return [];
    const map = new Map();
    for (const o of orders) {
        const bucket = roundPriceToAgg(o.price, agg);
        // Futures socket sends `quantity`; spot sends `remaining`. Support both.
        const qty = Number(o.quantity ?? o.remaining) || 0;
        const prev = map.get(bucket);
        if (prev) {
            prev.quantity  = (Number(prev.quantity)  || 0) + qty;
            prev.remaining = (Number(prev.remaining) || 0) + qty;
        } else {
            map.set(bucket, { ...o, price: bucket, quantity: qty, remaining: qty });
        }
    }
    return Array.from(map.values());
}

/** Binance-style order book header: Price | Amount | Total + swap (columns can swap). */
function OrderBookHeaderRow({ quoteCurrency, baseCurrency, swap, onSwap }) {
    const q = quoteCurrency || "USDT";
    const b = baseCurrency || "BTC";
    return (
        <tr>
            <th className="orderbook-th-sticky orderbook-th-price">Price ({q})</th>
            {!swap ? (
                <>
                    <th className="orderbook-th-sticky text-center orderbook-th-amount">Amount ({b})</th>
                    <th className="orderbook-th-sticky text-end orderbook-th-total">
                        <span className="orderbook-th-total-inner">
                            <span>Total ({q})</span>
                            <button type="button" className="orderbook-col-swap-btn" onClick={onSwap} aria-label="Swap amount and total">
                                <i className="ri-arrow-left-right-line" aria-hidden />
                            </button>
                        </span>
                    </th>
                </>
            ) : (
                <>
                    <th className="orderbook-th-sticky text-end orderbook-th-total">
                        <span className="orderbook-th-total-inner">
                            <span>Total ({q})</span>
                            <button type="button" className="orderbook-col-swap-btn" onClick={onSwap} aria-label="Swap amount and total">
                                <i className="ri-arrow-left-right-line" aria-hidden />
                            </button>
                        </span>
                    </th>
                    <th className="orderbook-th-sticky text-center orderbook-th-amount">Amount ({b})</th>
                </>
            )}
        </tr>
    );
}

function OrderBookBidRow({ data, fill, rowTotal, swap, onRowClick, formatOrderBookAggPrice, formatQuantity, formatOrderBookNotionalShort }) {
    const price = formatOrderBookAggPrice(data.price);
    const amt = formatQuantity(data.remaining ?? data.quantity);
    const depth = (
        <>
            <span className="orderbook-depth-fill orderbook-depth-fill--bid" style={{ width: `${fill}%` }} />
            <span className="orderbook-depth-text">{formatOrderBookNotionalShort(rowTotal)}</span>
        </>
    );
    const amountCell = <td className="text-center orderbook-col-num">{amt}</td>;
    const totalCell = <td className="text-end orderbook-depth-cell orderbook-depth-cell--bid">{depth}</td>;
    return (
        <tr style={{ cursor: "pointer" }} onClick={onRowClick}>
            <td className="orderbook-col-price orderbook-col-price--bid">{price}</td>
            {!swap ? (
                <>
                    {amountCell}
                    {totalCell}
                </>
            ) : (
                <>
                    {totalCell}
                    {amountCell}
                </>
            )}
        </tr>
    );
}

function OrderBookAskRow({ data, fill, rowTotal, swap, onRowClick, formatOrderBookAggPrice, formatQuantity, formatOrderBookNotionalShort }) {
    const price = formatOrderBookAggPrice(data.price);
    const amt = formatQuantity(data.remaining ?? data.quantity);
    const depth = (
        <>
            <span className="orderbook-depth-fill orderbook-depth-fill--ask" style={{ width: `${fill}%` }} />
            <span className="orderbook-depth-text">{formatOrderBookNotionalShort(rowTotal)}</span>
        </>
    );
    const amountCell = <td className="text-center orderbook-col-num">{amt}</td>;
    const totalCell = <td className="text-end orderbook-depth-cell orderbook-depth-cell--ask">{depth}</td>;
    return (
        <tr style={{ cursor: "pointer" }} onClick={onRowClick}>
            <td className="orderbook-col-price orderbook-col-price--ask">{price}</td>
            {!swap ? (
                <>
                    {amountCell}
                    {totalCell}
                </>
            ) : (
                <>
                    {totalCell}
                    {amountCell}
                </>
            )}
        </tr>
    );
}

/** accent: "market" = green/red from last move; "bid"/"ask" = buy/sell tab colors (Binance-style) */
function OrderBookMidPriceRow({
    buyprice,
    isPricePositive,
    priceChange,
    formatPriceThousands,
    showChangePct = true,
    afterList = false,
    accent = "market",
}) {
    const useGreenAccent = accent === "bid" ? true : accent === "ask" ? false : isPricePositive;
    return (
        <div
            className={`mrkt_trde_tab justify-content-between orderbook-mid-price orderbook-mid-price--ss ${afterList ? "orderbook-mid-price--after-list" : ""}`}
        >
            <div className="d-flex align-items-center gap-2 flex-wrap db_bl">
                <b className={useGreenAccent ? "text-green orderbook-mid-main" : "text-danger orderbook-mid-main"}>{formatPriceThousands(buyprice)}</b>
                <i className={`ri-arrow-${isPricePositive ? "up" : "down"}-line orderbook-mid-arrow ${useGreenAccent ? "text-green" : "text-danger"}`} />
                <span className="text-muted orderbook-mid-usd">${formatPriceThousands(buyprice)}</span>
                {showChangePct ? <span className="text-muted small orderbook-mid-pct">{Number(priceChange ?? 0).toFixed(2)}%</span> : null}
            </div>
            <div className="arrowright_icon">
                <i className="ri-arrow-right-s-line" />
            </div>
        </div>
    );
}

// function OrderBookBidAskFooter({ bidPct, askPct }) {
//     return (
//         <div className="orderbook_bid_ask_footer">
//             <div className="orderbook_bid_ask_labels">
//                 <span className="text-green">B {bidPct.toFixed(2)}%</span>
//                 <span className="text-danger">{askPct.toFixed(2)}% S</span>
//             </div>
//             <div className="orderbook_bid_ask_bar" aria-hidden>
//                 <span style={{ flex: bidPct }} className="orderbook_bid_ask_seg orderbook_bid_ask_seg--bid" />
//                 <span style={{ flex: askPct }} className="orderbook_bid_ask_seg orderbook_bid_ask_seg--ask" />
//             </div>
//         </div>
//     );
// }

function OrderBookBidAskFooter({ bidPct, askPct }) {
    return (
        <div className="orderbook_bid_ask_footer">
            <div className="orderbook_bid_ask_labels">
                <span className="text-green">B {bidPct.toFixed(2)}%</span>
                <span className="text-danger">{askPct.toFixed(2)}% S</span>
            </div>
            <div className="orderbook_bid_ask_bar" aria-hidden>
                <span style={{ flex: bidPct }} className="orderbook_bid_ask_seg orderbook_bid_ask_seg--bid" />
                <span style={{ flex: askPct }} className="orderbook_bid_ask_seg orderbook_bid_ask_seg--ask" />
            </div>
        </div>
    );
}

const SPOT_OPEN_ORDER_KINDS = [
    { id: "all", label: "All" },
    { id: "limit", label: "Limit" },
    { id: "market", label: "Market" },
];

/** Quote suffixes for compact pair symbols e.g. BTCUSDT → BTC/USDT */
const SPOT_OPEN_ORDER_PAIR_QUOTES = [
    "USDT", "USDC", "FDUSD", "BUSD", "TUSD", "USDD", "DAI", "BTC", "ETH", "BNB", "SOL", "TRX", "TRY", "EUR", "GBP",
];

function spotOpenOrderMarketLabel(item, selectedBase, selectedQuote) {
    if (!item) return "---/---";
    const compact = item.pair != null ? String(item.pair).trim().toUpperCase() : "";
    if (compact) {
        for (const q of SPOT_OPEN_ORDER_PAIR_QUOTES) {
            if (compact.endsWith(q) && compact.length > q.length) {
                return `${compact.slice(0, -q.length)}/${q}`;
            }
        }
        return compact;
    }
    const ask = item?.ask_currency || selectedBase || "---";
    const pay = item?.pay_currency || selectedQuote || "---";
    return item?.side === "SELL" ? `${pay}/${ask}` : `${ask}/${pay}`;
}

function spotOpenOrderTimestamp(item) {
    return item?.created_at || item?.updatedAt || item?.createdAt;
}

function spotOpenOrderFilledQty(item) {
    const v = item?.filled_quantity ?? item?.filled;
    if (v == null || v === "") return 0;
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
}

function spotOpenOrderRemainingQty(item) {
    const v = item?.remaining_quantity ?? item?.remaining;
    if (v == null || v === "") return 0;
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
}

function spotOpenOrderTypeLabel(item) {
    const t = item?.order_type || item?.type;
    if (t == null || String(t).trim() === "") return "—";
    return String(t).toUpperCase();
}

function spotOpenOrderTifLabel(item) {
    const tif = item?.time_in_force;
    if (tif == null || String(tif).trim() === "") return "—";
    return String(tif).toUpperCase();
}

function matchesOpenOrderKind(item, kind) {
    if (kind === "all") return true;
    const t = String(item?.type || "").toUpperCase();
    if (kind === "limit") return t === "LIMIT";
    if (kind === "market") return t === "MARKET";
    return true;
}

function formatSpotOpenOrderDateTime(item, format) {
    const ts = spotOpenOrderTimestamp(item);
    if (ts == null || ts === "") return "—";
    const m = moment(ts);
    return m.isValid() ? m.format(format) : "—";
}

/** Order history / executed orders (exchange payload uses `pair`, `*_quantity`, `executed_value`, etc.) */
function orderHistoryMarketLabel(item) {
    return spotOpenOrderMarketLabel(item, item?.ask_currency, item?.pay_currency);
}

function orderHistoryEventTime(item) {
    return item?.updated_at || item?.updatedAt || item?.created_at || item?.createdAt;
}

function formatOrderHistoryDateTime(item, format) {
    const ts = orderHistoryEventTime(item);
    if (ts == null || ts === "") return "—";
    const m = moment(ts);
    return m.isValid() ? m.format(format) : "—";
}

function orderHistoryQuoteCurrency(item) {
    const lbl = orderHistoryMarketLabel(item);
    const parts = lbl.split("/");
    if (parts.length === 2 && parts[1] && parts[1] !== "---") return parts[1];
    return item?.pay_currency || "";
}

function orderHistoryExecutedValueNum(item) {
    if (item?.executed_value != null && String(item.executed_value).trim() !== "") {
        const n = Number(item.executed_value);
        if (Number.isFinite(n)) return n;
    }
    const fill = spotOpenOrderFilledQty(item);
    const avg = Number(item?.avg_execution_price);
    if (Number.isFinite(avg) && fill > 0) return fill * avg;
    return null;
}

function orderHistoryStatusClass(status) {
    const s = String(status || "").toUpperCase();
    if (s === "FILLED" || s === "EXECUTED") return "success";
    if (s === "CANCELLED" || s === "REJECTED" || s === "EXPIRED") return "danger";
    if (s === "PARTIALLY_FILLED" || s === "PARTIALLY FILLED") return "warning";
    return "warning";
}

function orderHistoryStatusLabel(status) {
    const s = String(status || "").toUpperCase();
    if (s === "FILLED") return "EXECUTED";
    if (!status || String(status).trim() === "") return "—";
    return status;
}

/** Exchange spot payloads use `user_status`; legacy uses `status`. */
function spotOrderLifecycleStatus(item) {
    const s = item?.user_status ?? item?.status;
    if (s == null || String(s).trim() === "") return "";
    return String(s);
}

/**
 * Trade-history fills — each entry represents a single match between two
 * orders as returned by `GET /spot/v1/trades/my`. Payload shape:
 *   { _id, pair, side, price, quantity, quote_quantity,
 *     fee, fee_asset, fee_rate, tds, tds_asset, tds_rate,
 *     is_maker, order_id, is_self_match, executed_at }
 */
function tradeHistoryMarketLabel(item) {
    return spotOpenOrderMarketLabel(item);
}

function tradeHistoryBaseAsset(item) {
    return tradeHistoryMarketLabel(item).split("/")[0] || "";
}

function tradeHistoryQuoteAsset(item) {
    const parts = tradeHistoryMarketLabel(item).split("/");
    return parts.length === 2 ? parts[1] : "";
}

function formatTradeHistoryDateTime(item, format) {
    const ts = item?.executed_at || item?.executedAt || item?.created_at;
    if (ts == null || ts === "") return "—";
    const m = moment(ts);
    return m.isValid() ? m.format(format) : "—";
}

function tradeHistoryRoleLabel(item) {
    if (item?.is_maker === true) return "Maker";
    if (item?.is_maker === false) return "Taker";
    return "—";
}

const Margin = () => {
    const { getStatus } = usePlatformStatus();
    const isSpotDisabled = !getStatus('spot_trading').enabled;
    let params = useParams()
    let URL = params?.pairs?.split('_');

    const [authToken, setAuthToken] = useState(
        () => localStorage.getItem("token") || sessionStorage.getItem("token")
    );
    const token = authToken;

    useEffect(() => {
        const syncAuthToken = () => {
            setAuthToken(localStorage.getItem("token") || sessionStorage.getItem("token"));
        };
        window.addEventListener("agce-auth-changed", syncAuthToken);
        window.addEventListener("agce-auth-token-changed", syncAuthToken);
        return () => {
            window.removeEventListener("agce-auth-changed", syncAuthToken);
            window.removeEventListener("agce-auth-token-changed", syncAuthToken);
        };
    }, []);

    const [urlPath, setUrlPath] = useState(URL ? URL : []);
    const [search, setsearch] = useState('');
    const [AllData, setAllData] = useState({});
    const [BuyOrders, setBuyOrders] = useState([]);
    const [CoinPairDetails, setCoinPairDetails] = useState();
    const [RecentTrade, setRecentTrade] = useState([]);
    const [SellOrders, setSellOrders] = useState([]);
    const [buyamount, setbuyamount] = useState("");
    const [sellAmount, setsellAmount] = useState("");
    /** BASE | QUOTE — kept in sync with futuresContractUnitId for classic form. */
    const [buyAmountDenom, setbuyAmountDenom] = useState("BASE");
    const [sellAmountDenom, setsellAmountDenom] = useState("BASE");
    /** Amount (base) vs Value (quote) — shared by Limit / Market / Conditional. */
    const [futuresContractUnitId, setFuturesContractUnitId] = useState("amount_base");
    const [placingOrderSide, setPlacingOrderSide] = useState("");
    const futuresPlacingRef = useRef(false);
    const [infoPlaceOrder, setinfoPlaceOrder] = useState('LIMIT');
    const [coinFilter, setcoinFilter] = useState('ALL');
    const [BuyCoinBal, setBuyCoinBal] = useState();
    const [SellCoinBal, setSellCoinBal] = useState();
    const [openOrders, setopenOrders] = useState([]);
    const [orderType, setorderType] = useState('All');
    const [pastOrderType, setpastOrderType] = useState('All');
    const [spotOrderHistoryRows, setSpotOrderHistoryRows] = useState([]);
    const [spotTradeHistoryRows, setSpotTradeHistoryRows] = useState([]);
    const [, setOrderHistoryScrollPaused] = useState(false);
    const [, setTradeHistoryScrollPaused] = useState(false);
    const [tradeHistorySideFilter, setTradeHistorySideFilter] = useState('All');
    const [favCoins, setfavCoins] = useState([]);
    const [sellOrderPrice, setsellOrderPrice] = useState('');
    const [buyOrderPrice, setbuyOrderPrice] = useState('');
    const [buyStopPrice, setBuyStopPrice] = useState('');
    const [sellStopPrice, setSellStopPrice] = useState('');
    const [stopBuyPercent, setStopBuyPercent] = useState(0);
    const [stopSellPercent, setStopSellPercent] = useState(0);
    const [limitBuyPercent, setLimitBuyPercent] = useState(0);
    const [limitBuyFok, setLimitBuyFok] = useState(false);
    const [limitBuyIoc, setLimitBuyIoc] = useState(false);
    const [limitSellPercent, setLimitSellPercent] = useState(0);
    const [limitSellFok, setLimitSellFok] = useState(false);
    const [limitSellIoc, setLimitSellIoc] = useState(false);
    const [marketBuyPercent, setMarketBuyPercent] = useState(0);
    const [marketSellPercent, setMarketSellPercent] = useState(0);
    const [priceChange, setpriceChange] = useState('');
    const [changesHour, setChangesHour] = useState('');
    const [priceHigh, setpriceHigh] = useState('');
    const [priceLow, setpriceLow] = useState('');
    const [volume, setvolume] = useState('');
    const [showCoinList, setShowCoinList] = useState(false);
    /** True after socket sends order book fields (`buy_order` / `sell_order`) for the active pair. */
    const [orderBookSocketReady, setOrderBookSocketReady] = useState(false);
    const [baseCurId, setbaseCurId] = useState(null);
    const [quoteCurId, setquoteCurId] = useState(null);
    /** Reference bid/ask at pair load or pair change only (limit input fallback, sizing, market ref). */
    const [buyprice, setbuyprice] = useState('');
    const [sellPrice, setsellPrice] = useState('');
    /** Live last price for header / order book mid; does not drive the limit price field. */
    const [lastMarketPrice, setlastMarketPrice] = useState('');
    const [SelectedCoin, setSelectedCoin] = useState();
    const [isPricePositive, setIsPricePositive] = useState(true);
    /** Previous stream bid for last-move color; reset on pair change. */
    const lastStreamBidRef = useRef(null);
    const [showTab, setShowTab] = useState("chart");
    const [showBuySellTab, setShowBuySellTab] = useState("");
    const [orderBookActiveTab, setOrderBookActiveTab] = useState("orderbook");
    const [positionOrderTab, setPositionOrderTab] = useState("positions");
    const [openOrderKindTab, setOpenOrderKindTab] = useState("all");
    const [Coins, setCoins] = useState([]);
    const [expandedRowIndex, setExpandedRowIndex] = useState(null);
    const [showExecutedTrades, setShowExecutedTrades] = useState({});
    const spotOrderHistScopeRef = useRef(0);
    const spotTradeHistScopeRef = useRef(0);
    const orderHistPageLoadedRef = useRef(0);
    const tradeHistPageLoadedRef = useRef(0);
    const orderHistHasMoreRef = useRef(true);
    const tradeHistHasMoreRef = useRef(true);
    const orderHistAppendInFlightRef = useRef(false);
    const tradeHistAppendInFlightRef = useRef(false);

    // ── CONDITIONAL order — optional limit price that fires after the trigger ──
    const [buyOrderAfterTrigger,  setBuyOrderAfterTrigger]  = useState("");
    const [sellOrderAfterTrigger, setSellOrderAfterTrigger] = useState("");

    // ── Futures-specific state ────────────────────────────────────────────────
    const [futuresOpenOrders,     setFuturesOpenOrders]     = useState([]);
    const [futuresOrderHistory,   setFuturesOrderHistory]   = useState([]);
    const [futuresPositions,      setFuturesPositions]      = useState([]);
    const [futuresPositionHistory,setFuturesPositionHistory]= useState([]);
    const [futuresTradeHistory,   setFuturesTradeHistory]   = useState([]);
    const [futuresWalletHistory, setFuturesWalletHistory]   = useState([]);
    const [futuresWalletHistoryPagination, setFuturesWalletHistoryPagination] = useState({
        total: 0, page: 1, limit: 20, pages: 1,
    });
    const [walletHistoryFilters, setWalletHistoryFilters] = useState(FUTURES_WALLET_HISTORY_DEFAULT_FILTERS);
    const [futuresSocketBalance, setFuturesSocketBalance] = useState(null);
    const [futuresAccountPositions, setFuturesAccountPositions] = useState([]);
    const [futuresLeverage,       setFuturesLeverage]       = useState(1);
    const [futuresOrderRejectionOpen, setFuturesOrderRejectionOpen] = useState(false);
    const [futuresRiskWarningOpen, setFuturesRiskWarningOpen] = useState(false);
    const [futuresReduceOnly,     setFuturesReduceOnly]     = useState(false);
    const [futuresEffectiveAvailable, setFuturesEffectiveAvailable] = useState(null);
    const futuresScopeRef = useRef(0);
    /** Reset TP/SL draft when margin pair (base|quote) changes. */
    const marginTpSlPairKeySeenRef = useRef(null);
    /** Limit price seeded once per pair — not updated on live mark ticks. */
    const limitPriceSeededPairRef = useRef(null);
    const [showMobileFavouritesPopup, setShowMobileFavouritesPopup] = useState(false);
    const [isFavouritesOpen, setIsFavouritesOpen] = useState(false);
    const [isConditionalMenuOpen, setIsConditionalMenuOpen] = useState(false);

    // -------------------------------------------------------------------------
    // MARGIN — TP/SL conditional branch (`infoPlaceOrder === "TP_SL"`)
    // Fields: TP trigger (≤), TP limit (or market), SL trigger (≥), SL limit (or market), amount, % slider.
    // -------------------------------------------------------------------------
    const [marginFormTpSl, setMarginFormTpSl] = useState({
        tpTriggerPrice: "",
        tpLimitMarket: true,
        tpLimitPrice: "",
        slTriggerPrice: "",
        slLimitMarket: true,
        slLimitPrice: "",
        quantity: "",
        amountPercent: 0,
    });

    // -------------------------------------------------------------------------
    // MARGIN — TWAP branch (`infoPlaceOrder === "TWAP"`)
    // -------------------------------------------------------------------------
    const [marginFormTwap, setMarginFormTwap] = useState({
        totalQty: "",
        totalPercent: 0,
        durationHours: "",
        durationMinutes: "",
        priceVariance: "",
        priceVarianceUnit: "%",
        activationPrice: "",
        timeInterval: "",
        timeIntervalUnit: "Hour",
        perSubOrderQty: "",
    });

    // -------------------------------------------------------------------------
    // MARGIN — Iceberg Pro branch (`infoPlaceOrder === "ICEBERG_PRO"`)
    // -------------------------------------------------------------------------
    const [marginFormIcebergPro, setMarginFormIcebergPro] = useState({
        priceVariance: "",
        priceVarianceUnit: "%",
        activationPrice: "",
        totalQty: "",
        perSubOrderQty: "",
    });

    // -------------------------------------------------------------------------
    // MARGIN — Loop order branch (`infoPlaceOrder === "LOOP_ORDER"`)
    // -------------------------------------------------------------------------
    const [marginFormLoopOrder, setMarginFormLoopOrder] = useState({
        price: "",
        priceMode: "LAST_PRICE",
        takeProfitPct: "",
        stopLossPct: "",
        pauseAfterSlMin: "",
        amount: "",
        totalLoops: "",
        validFor: "1Day",
    });

    // -------------------------------------------------------------------------
    // MARGIN — Trailing stop branch (`infoPlaceOrder === "TRAILING_STOP"`)
    // -------------------------------------------------------------------------
    const [marginFormTrailingStop, setMarginFormTrailingStop] = useState({
        pullbackRatePct: "",
        pullbackUnit: "%",
        activationPrice: "",
        amount: "",
        amountPercent: 0,
        validFor: "1Day",
    });

    // -------------------------------------------------------------------------
    // MARGIN — Advanced limit branch (`infoPlaceOrder === "ADVANCED_LIMIT"`)
    // -------------------------------------------------------------------------
    const [marginFormAdvancedLimit, setMarginFormAdvancedLimit] = useState({
        limitPrice: "",
        quantity: "",
        postOnly: false,
        reduceOnly: false,
        displayQty: "",
    });

    // -------------------------------------------------------------------------
    // MARGIN — Generic conditional (`infoPlaceOrder === "CONDITIONAL"`)
    // -------------------------------------------------------------------------
    const [marginFormConditional, setMarginFormConditional] = useState({
        triggerPrice: "",
        orderMarket: true,
        orderLimitPrice: "",
        amount: "",
        amountUnitId: "amount_base",
        amountPercent: 0,
    });

    const { userDetails } = useContext(ProfileContext);
    const KycStatus = userDetails?.kycVerified;
    const {
        isConnected,
        futuresData,
        futuresPrice,
        futuresPortfolioOrders,
        futuresPortfolioPositions,
        subscribeToFutures,
        unsubscribeFromFutures,
    } = useContext(SocketContext);

    // Futures symbol — use the contract's own symbol field if available (e.g. "BTCUSDT-PERP")
    // Contracts are normalized with symbol = original server symbol, so just read it directly.
    const futuresSymbol = useMemo(() => {
        if (SelectedCoin?.symbol) return SelectedCoin.symbol;
        // Fallback: base_currency_id was set to the symbol string during normalization
        if (SelectedCoin?.base_currency_id && SelectedCoin.base_currency_id.includes("-PERP"))
            return SelectedCoin.base_currency_id;
        return null;
    }, [SelectedCoin?.symbol, SelectedCoin?.base_currency_id]);

    const [orderBookAggStep, setOrderBookAggStep] = useState(DEFAULT_ORDER_BOOK_AGG_OPTIONS[0]);
    const [orderBookAggOpen, setOrderBookAggOpen] = useState(false);
    /** both = split book, bids = buy only, asks = sell only */
    const [orderBookViewMode, setOrderBookViewMode] = useState("both");
    /** Swap Amount ↔ Total column order (Binance-style). */
    const [orderBookSwapAmountTotal, setOrderBookSwapAmountTotal] = useState(false);
    const [priceFieldFocus, setPriceFieldFocus] = useState(null);
    const [spotWallets, setSpotWallets] = useState([]);
    const [walletsLoading, setWalletsLoading] = useState(false);
    const navigate = useNavigate();

    const selectedPairApiCompact = useMemo(() => {
        if (!SelectedCoin?.base_currency || !SelectedCoin?.quote_currency) return "";
        const b = String(SelectedCoin.base_currency).trim();
        const q = String(SelectedCoin.quote_currency).trim();
        if (!b || !q) return "";
        return `${b}${q}`.toUpperCase();
    }, [SelectedCoin?.base_currency, SelectedCoin?.quote_currency]);

    const coinIconSrc = (path) => {
        if (!path) return "/images/coin_placeholder.png";
        const s = String(path);
        if (s.startsWith("http") || s.startsWith("/images/")) return s;
        return `${resolvePublicUploadUrl(s.replace(/^\//, ""))}`;
    };

    const safeToFixed8 = (value, fallback = "0") => {
        const parsed = parseFloat(value);
        if (!Number.isFinite(parsed)) return fallback;
        const s = parsed.toFixed(8).replace(/\.?0+$/, "");
        return s === "" ? "0" : s;
    };

    const getOpenOrderStopPrice = (item) => {
        const raw = item?.stop_price ?? item?.stopPrice ?? item?.trigger_price ?? item?.triggerPrice;
        if (raw == null || String(raw).trim() === "") return "—";
        const unwrapped =
            raw && typeof raw === "object" && "$numberDecimal" in raw ? raw.$numberDecimal : raw;
        return safeToFixed8(unwrapped, "—");
    };

    const getOpenOrderPair = (item) =>
        spotOpenOrderMarketLabel(item, SelectedCoin?.base_currency, SelectedCoin?.quote_currency);

    // eslint-disable-next-line no-unused-vars
    const reloadSpotOrderHistoryPage1 = useCallback(async () => {
        if (DISABLE_ALL_NETWORK || !token) return;
        const pair = selectedPairApiCompact;
        if (!pair) return;
        const scope = spotOrderHistScopeRef.current;
        try {
            const res = await AuthService.spotMeOrdersHistory({
                page: 1,
                page_size: SPOT_HIST_PAGE_SIZE,
                pair,
            });
            if (spotOrderHistScopeRef.current !== scope) return;
            const items = res?.success && Array.isArray(res?.data?.items) ? res.data.items : [];
            const norm = normalizeSpotOrdersHistoryItemsForTrade(items);
            setSpotOrderHistoryRows((prev) => {
                const freshIds = new Set(norm.map((r) => String(r._id)));
                const tail = (prev || []).filter((r) => !freshIds.has(String(r._id)));
                return dedupeSpotHistoryById([...norm, ...tail], (r) => r._id);
            });
            if (orderHistPageLoadedRef.current <= 1) {
                orderHistPageLoadedRef.current = norm.length ? 1 : 0;
                orderHistHasMoreRef.current = norm.length >= SPOT_HIST_PAGE_SIZE;
            } else if (norm.length >= SPOT_HIST_PAGE_SIZE) {
                orderHistHasMoreRef.current = true;
            }
        } catch {
            /* keep existing rows */
        }
    }, [token, selectedPairApiCompact]);

    const appendSpotOrderHistory = useCallback(async () => {
        if (DISABLE_ALL_NETWORK || !token) return;
        const pair = selectedPairApiCompact;
        if (!pair || !orderHistHasMoreRef.current || orderHistAppendInFlightRef.current) return;
        if (orderHistPageLoadedRef.current < 1) return;

        const scope = spotOrderHistScopeRef.current;
        const nextPage = orderHistPageLoadedRef.current + 1;
        orderHistAppendInFlightRef.current = true;
        try {
            const res = await AuthService.spotMeOrdersHistory({
                page: nextPage,
                page_size: SPOT_HIST_PAGE_SIZE,
                pair,
            });
            if (spotOrderHistScopeRef.current !== scope) return;
            const items = res?.success && Array.isArray(res?.data?.items) ? res.data.items : [];
            const norm = normalizeSpotOrdersHistoryItemsForTrade(items);
            orderHistHasMoreRef.current = norm.length >= SPOT_HIST_PAGE_SIZE;
            if (norm.length === 0) return;
            orderHistPageLoadedRef.current = nextPage;
            setSpotOrderHistoryRows((prev) =>
                dedupeSpotHistoryById([...(prev || []), ...norm], (r) => r._id)
            );
        } finally {
            orderHistAppendInFlightRef.current = false;
        }
    }, [token, selectedPairApiCompact]);

    // eslint-disable-next-line no-unused-vars
    const reloadSpotTradeHistoryPage1 = useCallback(async () => {
        if (DISABLE_ALL_NETWORK || !token) return;
        const pair = selectedPairApiCompact;
        if (!pair) return;
        const scope = spotTradeHistScopeRef.current;
        try {
            const res = await AuthService.spotMeTrades({
                page: 1,
                page_size: SPOT_HIST_PAGE_SIZE,
                pair,
            });
            if (spotTradeHistScopeRef.current !== scope) return;
            const items = res?.success && Array.isArray(res?.data?.items) ? res.data.items : [];
            const norm = normalizeSpotTradeFillsItemsForTrade(items);
            setSpotTradeHistoryRows((prev) => {
                const freshIds = new Set(norm.map((r) => String(r._id)));
                const tail = (prev || []).filter((r) => !freshIds.has(String(r._id)));
                return dedupeSpotHistoryById([...norm, ...tail], (r) => r._id);
            });
            if (tradeHistPageLoadedRef.current <= 1) {
                tradeHistPageLoadedRef.current = norm.length ? 1 : 0;
                tradeHistHasMoreRef.current = norm.length >= SPOT_HIST_PAGE_SIZE;
            } else if (norm.length >= SPOT_HIST_PAGE_SIZE) {
                tradeHistHasMoreRef.current = true;
            }
        } catch {
            /* keep existing rows */
        }
    }, [token, selectedPairApiCompact]);

    const appendSpotTradeHistory = useCallback(async () => {
        if (DISABLE_ALL_NETWORK || !token) return;
        const pair = selectedPairApiCompact;
        if (!pair || !tradeHistHasMoreRef.current || tradeHistAppendInFlightRef.current) return;
        if (tradeHistPageLoadedRef.current < 1) return;

        const scope = spotTradeHistScopeRef.current;
        const nextPage = tradeHistPageLoadedRef.current + 1;
        tradeHistAppendInFlightRef.current = true;
        try {
            const res = await AuthService.spotMeTrades({
                page: nextPage,
                page_size: SPOT_HIST_PAGE_SIZE,
                pair,
            });
            if (spotTradeHistScopeRef.current !== scope) return;
            const items = res?.success && Array.isArray(res?.data?.items) ? res.data.items : [];
            const norm = normalizeSpotTradeFillsItemsForTrade(items);
            tradeHistHasMoreRef.current = norm.length >= SPOT_HIST_PAGE_SIZE;
            if (norm.length === 0) return;
            tradeHistPageLoadedRef.current = nextPage;
            setSpotTradeHistoryRows((prev) =>
                dedupeSpotHistoryById([...(prev || []), ...norm], (r) => r._id)
            );
        } finally {
            tradeHistAppendInFlightRef.current = false;
        }
    }, [token, selectedPairApiCompact]);

    const handleSpotOrderHistoryScroll = useCallback(
        (e) => {
            const el = e?.currentTarget;
            if (!(el instanceof Element)) return;
            const paused = el.scrollTop > SPOT_HIST_SCROLL_TOP_EPS;
            setOrderHistoryScrollPaused((prev) => (prev === paused ? prev : paused));
            if (!orderHistHasMoreRef.current || orderHistAppendInFlightRef.current) return;
            const gap = el.scrollHeight - el.scrollTop - el.clientHeight;
            if (gap < SPOT_HIST_LOAD_MORE_PX) void appendSpotOrderHistory();
        },
        [appendSpotOrderHistory]
    );

    const handleSpotTradeHistoryScroll = useCallback(
        (e) => {
            const el = e?.currentTarget;
            if (!(el instanceof Element)) return;
            const paused = el.scrollTop > SPOT_HIST_SCROLL_TOP_EPS;
            setTradeHistoryScrollPaused((prev) => (prev === paused ? prev : paused));
            if (!tradeHistHasMoreRef.current || tradeHistAppendInFlightRef.current) return;
            const gap = el.scrollHeight - el.scrollTop - el.clientHeight;
            if (gap < SPOT_HIST_LOAD_MORE_PX) void appendSpotTradeHistory();
        },
        [appendSpotTradeHistory]
    );

    useEffect(() => {
        spotOrderHistScopeRef.current += 1;
        spotTradeHistScopeRef.current += 1;
        setSpotOrderHistoryRows([]);
        setSpotTradeHistoryRows([]);
        orderHistPageLoadedRef.current = 0;
        tradeHistPageLoadedRef.current = 0;
        orderHistHasMoreRef.current = true;
        tradeHistHasMoreRef.current = true;
        setExpandedRowIndex(null);
        setOrderHistoryScrollPaused(false);
        setTradeHistoryScrollPaused(false);
    }, [selectedPairApiCompact]);

    useEffect(() => {
        if (params?.pairs) {
            setUrlPath(params.pairs.split("_"));
        }
    }, [params?.pairs]);

    useEffect(() => {
        try {
            if (!localStorage.getItem(FUTURES_RISK_WARNING_STORAGE_KEY)) {
                setFuturesRiskWarningOpen(true);
            }
        } catch {
            setFuturesRiskWarningOpen(true);
        }
    }, []);

    const acknowledgeFuturesRiskWarning = useCallback(() => {
        try {
            localStorage.setItem(FUTURES_RISK_WARNING_STORAGE_KEY, "1");
        } catch {
            // ignore storage failures
        }
        setFuturesRiskWarningOpen(false);
    }, []);

    useEffect(() => {
        if (!orderBookAggOpen) return undefined;
        const onDown = (e) => {
            if (!e.target.closest?.(".orderbook_agg_dd")) {
                setOrderBookAggOpen(false);
            }
        };
        const onKey = (e) => {
            if (e.key === "Escape") setOrderBookAggOpen(false);
        };
        document.addEventListener("mousedown", onDown);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDown);
            document.removeEventListener("keydown", onKey);
        };
    }, [orderBookAggOpen]);

    const orderBookAggOptions = useMemo(
        () => getOrderBookAggOptionsForPair(SelectedCoin?.tick_size),
        [SelectedCoin?.tick_size]
    );

    // Always use the finest aggregation step (tick) for the pair = most order book rows / maximum detail
    useEffect(() => {
        if (!orderBookAggOptions.length) return;
        setOrderBookAggStep(orderBookAggOptions[0]);
    }, [orderBookAggOptions, SelectedCoin?.base_currency_id, SelectedCoin?.quote_currency_id]);

    // ── futures:update handler ────────────────────────────────────────────────
    // futuresData is set by SocketContext when the server emits futures:update.
    // Two shapes per the doc:
    //   { type: "contracts_only", contracts: [...] }  → pair list (one-time response)
    //   { type: "contract_data", contract: {...}, buy_order, sell_order, recent_trades, open_orders, executed_trades, balance }
    useEffect(() => {
        if (!futuresData) return;

        // contracts_only: server responded to futures:subscribe({}) — populate pair list
        if (futuresData.type === "contracts_only") {
            const contracts = (futuresData.contracts || []).map((c) => ({
                ...c,
                base_currency:      c.base_asset,
                quote_currency:     c.quote_asset,
                base_currency_id:   c.symbol,
                quote_currency_id:  c.quote_asset,
                buy_price:          c.mark_price ?? c.bid_price ?? c.last_price ?? null,
                sell_price:         c.ask_price  ?? c.mark_price ?? c.last_price ?? null,
                change_percentage:  c.change_percentage ?? null,
                change:             c.price_change_24h ?? null,
                high:               c.high_24h  ?? null,
                low:                c.low_24h   ?? null,
                volume:             c.volume_24h ?? null,
            }));
            setCoins(contracts);
            setAllData((prev) => ({ ...prev, pairs: contracts }));

            // Auto-subscribe to the first contract to get live data immediately
            if (contracts.length > 0 && !SelectedCoin) {
                const first = contracts[0];
                subscribeToFutures({ symbol: first.symbol });
            }
            return;
        }

        // contract_data: continuous update for the subscribed symbol
        if (futuresData.type === "contract_data") {
            const c = futuresData.contract;

            // Guard: skip if this update is for a different symbol than selected
            if (futuresSymbol && c?.symbol && c.symbol !== futuresSymbol) return;

            // Balance + account panel (socket — no REST /v1/futures/account poll)
            if (futuresData.balance) {
                applyFuturesSocketBalance(futuresData.balance, {
                    setBuyCoinBal,
                    setSellCoinBal,
                    setFuturesEffectiveAvailable,
                    setFuturesSocketBalance,
                });
            }

            // Orderbook
            const hasBuyArr  = Array.isArray(futuresData.buy_order);
            const hasSellArr = Array.isArray(futuresData.sell_order);
            if (hasBuyArr)  setBuyOrders(futuresData.buy_order);
            if (hasSellArr) setSellOrders(futuresData.sell_order);
            if (hasBuyArr || hasSellArr) setOrderBookSocketReady(true);

            // Open orders (symbol-scoped legacy state for spot-compat props)
            if (futuresData.open_orders !== undefined) {
                setopenOrders(futuresData.open_orders || []);
            }

            // Recent trades
            if (Array.isArray(futuresData.recent_trades)) {
                setRecentTrade(futuresData.recent_trades);
            }

            // Contract — price + 24h stats
            if (c) {
                // Live price from contract payload
                const livePrice = c.mark_price ?? c.last_price ?? c.bid_price;
                if (livePrice != null) {
                    const price = parseFloat(livePrice);
                    if (Number.isFinite(price) && price > 0) {
                        if (lastStreamBidRef.current != null) {
                            const prev = Number(lastStreamBidRef.current);
                            if (price > prev) setIsPricePositive(true);
                            else if (price < prev) setIsPricePositive(false);
                        }
                        lastStreamBidRef.current = price;
                        setlastMarketPrice(price);
                        setbuyprice(price);
                    }
                }

                if (c.change_percentage != null) setpriceChange(c.change_percentage);
                if (c.price_change_24h  != null) setChangesHour(c.price_change_24h);
                if (c.high_24h  != null) setpriceHigh(c.high_24h);
                if (c.low_24h   != null) setpriceLow(c.low_24h);
                if (c.volume_24h != null) setvolume(c.volume_24h);

                setSelectedCoin((prev) => prev ? {
                    ...prev,
                    change_percentage: c.change_percentage  ?? prev.change_percentage,
                    change:            c.price_change_24h   ?? prev.change,
                    high:   c.high_24h   ?? prev.high,
                    low:    c.low_24h    ?? prev.low,
                    volume: c.volume_24h ?? prev.volume,
                    volume_24h: c.volume_24h ?? prev.volume_24h,
                    volume_24h_quote: c.volume_24h_quote ?? prev.volume_24h_quote,
                    mark_price:   c.mark_price   ?? prev.mark_price,
                    index_price:  c.index_price  ?? prev.index_price,
                    last_price:   c.last_price   ?? prev.last_price,
                    tick_size:    c.tick_size    ?? prev.tick_size,
                    step_size:    c.step_size    ?? prev.step_size,
                    min_order_qty: c.min_order_qty ?? prev.min_order_qty,
                    min_notional: c.min_notional ?? prev.min_notional,
                    max_leverage: c.max_leverage ?? prev.max_leverage,
                    leverage_tiers: c.leverage_tiers ?? prev.leverage_tiers,
                    funding_rate: c.funding_rate ?? prev.funding_rate,
                    next_funding_time: c.next_funding_time ?? prev.next_funding_time,
                    funding_interval_hours: c.funding_interval_hours ?? prev.funding_interval_hours,
                    funding_rate_cap: c.funding_rate_cap ?? prev.funding_rate_cap,
                    funding_rate_floor: c.funding_rate_floor ?? prev.funding_rate_floor,
                    previous_funding_rate:
                        c.previous_funding_rate ?? c.prev_funding_rate ?? c.last_funding_rate ?? prev.previous_funding_rate,
                    open_interest: c.open_interest ?? prev.open_interest,
                    taker_fee_rate: c.taker_fee_rate ?? prev.taker_fee_rate,
                } : prev);

                // Keep pair list prices in sync so the sidebar stays live
                setAllData((prev) => {
                    if (!prev?.pairs?.length) return prev;
                    const updated = prev.pairs.map((p) =>
                        p.symbol === c.symbol
                            ? {
                                ...p,
                                buy_price:         c.mark_price       ?? c.bid_price  ?? c.last_price ?? p.buy_price,
                                sell_price:        c.ask_price        ?? c.mark_price ?? c.last_price ?? p.sell_price,
                                change_percentage: c.change_percentage ?? p.change_percentage,
                                change:            c.price_change_24h  ?? p.change,
                                high:   c.high_24h   ?? p.high,
                                low:    c.low_24h    ?? p.low,
                                volume: c.volume_24h ?? p.volume,
                            }
                            : p
                    );
                    return { ...prev, pairs: updated };
                });
            }
            return;
        }

        // Fallback: handle legacy shape without explicit type field
        if (futuresSymbol && futuresData.symbol && futuresData.symbol !== futuresSymbol) return;

        if (futuresData.balance) {
            applyFuturesSocketBalance(futuresData.balance, {
                setBuyCoinBal,
                setSellCoinBal,
                setFuturesEffectiveAvailable,
                setFuturesSocketBalance,
            });
        }

        const hasBuyArr  = Array.isArray(futuresData.buy_order);
        const hasSellArr = Array.isArray(futuresData.sell_order);
        if (hasBuyArr)  setBuyOrders(futuresData.buy_order);
        if (hasSellArr) setSellOrders(futuresData.sell_order);
        if (hasBuyArr || hasSellArr) setOrderBookSocketReady(true);

        if (futuresData.open_orders !== undefined) {
            setopenOrders(futuresData.open_orders || []);
        }

        if (futuresData.recent_trades) {
            setRecentTrade(futuresData.recent_trades);
        }

        if (futuresData.contract) {
            const c = futuresData.contract;
            if (c.change_percentage != null) setpriceChange(c.change_percentage);
            if (c.price_change_24h  != null) setChangesHour(c.price_change_24h);
            if (c.high_24h  != null) setpriceHigh(c.high_24h);
            if (c.low_24h   != null) setpriceLow(c.low_24h);
            if (c.volume_24h != null) setvolume(c.volume_24h);
            setSelectedCoin((prev) => prev ? {
                ...prev,
                change_percentage: c.change_percentage ?? prev.change_percentage,
                change:            c.price_change_24h  ?? prev.change,
                high:   c.high_24h   ?? prev.high,
                low:    c.low_24h    ?? prev.low,
                volume: c.volume_24h ?? prev.volume,
                volume_24h: c.volume_24h ?? prev.volume_24h,
                volume_24h_quote: c.volume_24h_quote ?? prev.volume_24h_quote,
                mark_price:   c.mark_price   ?? prev.mark_price,
                index_price:  c.index_price  ?? prev.index_price,
                tick_size:    c.tick_size    ?? prev.tick_size,
                step_size:    c.step_size    ?? prev.step_size,
                min_order_qty: c.min_order_qty ?? prev.min_order_qty,
                min_notional: c.min_notional ?? prev.min_notional,
                funding_rate: c.funding_rate ?? prev.funding_rate,
                next_funding_time: c.next_funding_time ?? prev.next_funding_time,
                funding_interval_hours: c.funding_interval_hours ?? prev.funding_interval_hours,
                funding_rate_cap: c.funding_rate_cap ?? prev.funding_rate_cap,
                funding_rate_floor: c.funding_rate_floor ?? prev.funding_rate_floor,
                previous_funding_rate:
                    c.previous_funding_rate ?? c.prev_funding_rate ?? c.last_funding_rate ?? prev.previous_funding_rate,
                open_interest: c.open_interest ?? prev.open_interest,
            } : prev);
        }
        // subscribeToFutures / SelectedCoin intentionally omitted — this effect only applies ticker ticks.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [futuresData, futuresSymbol]);

    // Portfolio open orders / positions from socket (futures:open_orders, futures:open_positions, futures:update)
    useEffect(() => {
        if (!token) {
            setFuturesOpenOrders([]);
            setFuturesPositions([]);
            setFuturesAccountPositions([]);
            setFuturesSocketBalance(null);
            return;
        }
        if (Array.isArray(futuresPortfolioOrders)) {
            setFuturesOpenOrders(normalizeFuturesSocketOrders(futuresPortfolioOrders));
        }
        if (Array.isArray(futuresPortfolioPositions)) {
            const positions = normalizeFuturesSocketPositions(futuresPortfolioPositions);
            setFuturesPositions(positions);
            setFuturesAccountPositions(positions);
        }
    }, [token, futuresPortfolioOrders, futuresPortfolioPositions]);

    const futuresAccounts = useMemo(
        () => futuresAccountsFromSocketBalance(futuresSocketBalance, futuresAccountPositions),
        [futuresSocketBalance, futuresAccountPositions]
    );

    // ── futures:price — sub-second price tick for display ────────────────────
    // Fires on optional sub-second price ticks. Only updates price state, not orderbook.
    useEffect(() => {
        if (!futuresPrice) return;
        if (futuresSymbol && futuresPrice.symbol && futuresPrice.symbol !== futuresSymbol) return;

        const p = futuresPrice.mark_price ?? futuresPrice.last_price;
        if (p == null) return;

        const price = parseFloat(p);
        if (!Number.isFinite(price) || price <= 0) return;

        if (lastStreamBidRef.current != null) {
            const prev = Number(lastStreamBidRef.current);
            if (price > prev) setIsPricePositive(true);
            else if (price < prev) setIsPricePositive(false);
        }
        lastStreamBidRef.current = price;
        setlastMarketPrice(price);
        setbuyprice(price);
    }, [futuresPrice, futuresSymbol]);

    // ── Subscribe to futures when symbol changes ──────────────────────────────
    useEffect(() => {
        if (!isConnected) return;
        // Subscribe with symbol — server sends full updates every 1.5s
        subscribeToFutures(futuresSymbol ? { symbol: futuresSymbol } : {});
        return () => {
            unsubscribeFromFutures();
        };
    }, [futuresSymbol, isConnected, subscribeToFutures, unsubscribeFromFutures]);

    // ── Pair switch: clear stale data ─────────────────────────────────────────
    useEffect(() => {
        setOrderBookSocketReady(false);
        setBuyOrders([]);
        setSellOrders([]);
        setBuyCoinBal(null);
        setSellCoinBal(null);
        setFuturesEffectiveAvailable(null);
        setbuyprice('');
        setlastMarketPrice('');
        lastStreamBidRef.current = null;
        limitPriceSeededPairRef.current = null;
        setbuyOrderPrice('');
        setsellOrderPrice('');
    }, [baseCurId, quoteCurId]);

    useEffect(() => {
        if (!SelectedCoin && CoinPairDetails && CoinPairDetails.length > 0 && isConnected) {
            let Pair;
            let filteredData;
            if (urlPath?.length > 0) {
                filteredData = CoinPairDetails?.filter?.((item) => {
                    return urlPath[0]?.includes(item?.base_currency) && urlPath[1]?.includes(item?.quote_currency);
                });
            }
            if (filteredData?.length > 0) {
                Pair = filteredData[0];
            } else {
                Pair = CoinPairDetails[0];
            }
            if (!Pair) return;
            navigate(`/futures_trade/${Pair?.base_currency}_${Pair?.quote_currency}`);
            limitPriceSeededPairRef.current = `${Pair?.base_currency_id}|${Pair?.quote_currency_id}`;
            setSelectedCoin(Pair);
            setbaseCurId(Pair?.base_currency_id);
            setquoteCurId(Pair?.quote_currency_id);
            setbuyprice(Pair?.buy_price);
            setsellPrice(Pair?.sell_price);
            seedLimitPrice(Pair?.buy_price ?? Pair?.sell_price);
            lastStreamBidRef.current = null;
            setlastMarketPrice(Pair?.buy_price);
            setbuyamount("");
            setsellAmount("");
            setBuyStopPrice("");
            setSellStopPrice("");
            setBuyOrderAfterTrigger("");
            setSellOrderAfterTrigger("");
            setStopBuyPercent(0);
            setStopSellPercent(0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [CoinPairDetails, isConnected, urlPath, navigate]);

    // On connect, request contracts list if not yet populated
    useEffect(() => {
        if (isConnected && !CoinPairDetails?.length) {
            subscribeToFutures({}); // {} → server responds with contracts_only
        }
    }, [isConnected, CoinPairDetails?.length, subscribeToFutures]);

    const pastOrders = useMemo(() => {
        const list = (spotOrderHistoryRows || []).filter(
            (item) => pastOrderType === item?.side || pastOrderType === "All"
        );
        return [...list].sort((a, b) => {
            const ta = Date.parse(a?.created_at || a?.updated_at || "") || 0;
            const tb = Date.parse(b?.created_at || b?.updated_at || "") || 0;
            return tb - ta;
        });
    }, [pastOrderType, spotOrderHistoryRows]);

    // NOTE: Order History / Trade History tabs on the futures page render futures
    // data (futuresOrderHistory / futuresTradeHistory) and are polled by the
    // futures history effect below. The spot-history polling that previously ran
    // here was leftover from the spot trade page and hit the spot API on each
    // interval — it is intentionally disabled on this page.

    // ********* Update Buy Sell 24HChange High Low Volume Price********** //
    useEffect(() => {
        let filteredData = AllData?.pairs?.filter((item) => {
            return item?.base_currency_id === SelectedCoin?.base_currency_id
        })
        if (filteredData?.length) {
            setpriceChange(filteredData[0]?.change_percentage);
            setChangesHour(filteredData[0]?.change);
            setpriceHigh(filteredData[0]?.high);
            setpriceLow(filteredData[0]?.low);
            setvolume(filteredData[0]?.volume);

        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [AllData]);


    // ********* Update Buy Sell 24HChange High Low Volume Price********** //
    useEffect(() => {
        if (AllData && SelectedCoin) {
            let filteredData = AllData?.pairs?.filter((item) => {
                return item?.base_currency_id === SelectedCoin?.base_currency_id && item?.quote_currency_id === SelectedCoin?.quote_currency_id
            })
            if (filteredData?.length) {
                const p = filteredData[0]?.buy_price;
                if (p != null) {
                    if (lastStreamBidRef.current != null) {
                        const current = Number(p);
                        const previous = Number(lastStreamBidRef.current);
                        if (current > previous) {
                            setIsPricePositive(true);
                        } else if (current < previous) {
                            setIsPricePositive(false);
                        }
                    }
                    lastStreamBidRef.current = p;
                    setlastMarketPrice(p);
                }
                setpriceChange(filteredData[0]?.change_percentage);
                setChangesHour(filteredData[0]?.change);
                setpriceHigh(filteredData[0]?.high);
                setpriceLow(filteredData[0]?.low);
                setvolume(filteredData[0]?.volume);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [AllData]);


    // ********* Search Coins ********** //
    useEffect(() => {
        let filteredData = AllData?.pairs?.filter((item) => {
            return item?.base_currency?.toLowerCase().includes(search?.toLowerCase()) || item?.quote_currency?.toLowerCase().includes(search?.toLowerCase())
        })
        setCoinPairDetails(filteredData)
    }, [search, AllData]);

    // Set default coin filter to first quote currency
    useEffect(() => {
        if (CoinPairDetails?.length > 0 && coinFilter === 'ALL') {
            const firstQuoteCurrency = CoinPairDetails[0]?.quote_currency;
            if (firstQuoteCurrency) {
                setcoinFilter(firstQuoteCurrency);
            }
        }
    }, [CoinPairDetails, coinFilter]);



    const fetchSpotWallets = async () => {
        if (!token) return;
        setWalletsLoading(true);
        try {
            const result = await AuthService.getUserfunds("spot");
            if (result?.success) {
                const walletsWithBalance = (result.data || [])
                    .filter((w) => parseFloat(w.balance) > 0)
                    .sort((a, b) => parseFloat(b.balance) - parseFloat(a.balance));
                setSpotWallets(walletsWithBalance);
            }
        } catch (error) {
            console.error("Failed to fetch spot wallets:", error);
        } finally {
            setWalletsLoading(false);
        }
    };

    const applyFuturesContractUnit = useCallback((id) => {
        const unitId = normalizeFuturesContractUnitId(id);
        const denom = denomFromFuturesContractUnitId(unitId);
        setFuturesContractUnitId(unitId);
        setbuyAmountDenom(denom);
        setsellAmountDenom(denom);
    }, []);

    /** Classic futures tabs share contract unit; reset only for UI-only advanced forms. */
    useEffect(() => {
        if (["LIMIT", "MARKET", "CONDITIONAL"].includes(infoPlaceOrder)) {
            return;
        }
        if (buyAmountDenom === "QUOTE") setbuyamount("");
        if (sellAmountDenom === "QUOTE") setsellAmount("");
        setbuyAmountDenom("BASE");
        setsellAmountDenom("BASE");
        setFuturesContractUnitId("amount_base");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [infoPlaceOrder]);

    const classicFuturesValueUnit = isFuturesValueUnit(futuresContractUnitId);

    /** Margin UI no longer offers Stop Limit / Stop Market — normalize legacy tab state. */
    useEffect(() => {
        setinfoPlaceOrder((prev) => (prev === "STOP_LIMIT" || prev === "STOP_MARKET" ? "LIMIT" : prev));
    }, []);


    const cancelOrder = async (orderId) => {
        await AuthService.cancelOrder(orderId).then((result) => {
            if (result?.success) {
                LoaderHelper.loaderStatus(false);
                try {
                    alertSuccessMessage('Order Cancelled Successfully');
                } catch (error) {
                    LoaderHelper.loaderStatus(false);
                }
            } else {
                LoaderHelper.loaderStatus(false);
                alertErrorMessage(result?.message);
            }
        });
    };

    // ── Futures order helpers ─────────────────────────────────────────────────

    /**
     * Validate and build the futures order payload according to POST /v1/futures/orders.
     * Supported order_type values: LIMIT | MARKET | CONDITIONAL
     * `extras` comes from TradeCenterSection local state (leverage, tif, slippage, postOnly, tp/sl, etc.)
     */
    const [orderFieldError, setOrderFieldError] = useState(null);
    const showFieldError = (side, field, text) => setOrderFieldError({ side, field, text });
    useEffect(() => {
        setOrderFieldError((prev) => (prev?.field === "amount" ? null : prev));
    }, [buyamount, sellAmount]);
    useEffect(() => {
        setOrderFieldError((prev) => (
            prev?.field === "price" || prev?.field === "orderPrice" ? null : prev
        ));
    }, [buyOrderPrice, sellOrderPrice, buyOrderAfterTrigger, sellOrderAfterTrigger]);
    useEffect(() => {
        setOrderFieldError((prev) => (prev?.field === "trigger" ? null : prev));
    }, [buyStopPrice, sellStopPrice]);

    const buildFuturesOrderPayload = (uiSide, extras = {}) => {
        const symbol = SelectedCoin?.symbol || SelectedCoin?.base_currency_id;
        if (!symbol) { alertErrorMessage(futuresErrSelectPair()); return null; }

        const order_type = infoPlaceOrder;
        if (!["LIMIT", "MARKET", "CONDITIONAL"].includes(order_type)) {
            alertErrorMessage(futuresErrOrderType());
            return null;
        }

        const apiSide = String(uiSide).toUpperCase(); // "BUY" or "SELL"
        const isBuy   = apiSide === "BUY";

        const tickSize    = Number(SelectedCoin?.tick_size)     || 0.01;
        const stepSize    = Number(SelectedCoin?.step_size)     || 0.001;
        const minQty      = Number(SelectedCoin?.min_order_qty) || stepSize;
        const maxQty      = Number(SelectedCoin?.max_order_qty) || 1000;
        const minNotional = Number(SelectedCoin?.min_notional)  || 0;

        // Quantity — Value unit is USDT notional in the UI; API expects base qty on step_size.
        const rawQty = isBuy ? buyamount : sellAmount;

        const isQuoteSize = resolveFuturesAmountIsQuote({
            orderType: order_type,
            isBuy,
            contractUnitId: extras.amountUnitId ?? futuresContractUnitId,
            buyAmountDenom,
            sellAmountDenom,
        });

        const markPrice = Number(SelectedCoin?.mark_price) || Number(buyprice) || Number(sellPrice) || 0;

        const priceForQtyConversion = resolveFuturesPriceForQtyConversion({
            orderType: order_type,
            isBuy,
            markPrice,
            buyOrderPrice,
            sellOrderPrice,
            buyStopPrice,
            sellStopPrice,
            buyOrderAfterTrigger,
            sellOrderAfterTrigger,
            buyprice,
            sellPrice,
        });

        const { rawVal, baseQty: qtyFromSizing } = convertFuturesAmountToBaseQty({
            rawAmount: rawQty,
            isQuoteSize,
            priceForConversion: priceForQtyConversion,
            stepSize,
        });

        const amountLimit = (kind, baseValue) => {
            const px = Number(priceForQtyConversion) || Number(markPrice) || 0;
            const mins = effectiveOrderMinimum({
                price: px,
                stepSize,
                tickSize,
                minOrderQty: minQty,
                minNotional,
            });
            const unit = isQuoteSize
                ? (SelectedCoin?.quote_currency || SelectedCoin?.margin_asset || "USDT")
                : (SelectedCoin?.base_currency || "BTC");
            if (kind === "Min") {
                return amountLimitLabel("Min", isQuoteSize ? mins.minQuote : mins.minBase, isQuoteSize ? tickSize : stepSize, unit);
            }
            const shown = isQuoteSize && px > 0 ? Number(baseValue) * px : Number(baseValue);
            return amountLimitLabel("Max", shown, isQuoteSize ? tickSize : stepSize, unit);
        };

        if (!Number.isFinite(rawVal) || rawVal <= 0) {
            showFieldError(apiSide, "amount", "Enter amount");
            return null;
        }

        if (isQuoteSize && (!Number.isFinite(priceForQtyConversion) || priceForQtyConversion <= 0)) {
            showFieldError(apiSide, "price", "Enter price");
            return null;
        }

        let qty = qtyFromSizing;
        if (!Number.isFinite(qty) || qty <= 0) {
            showFieldError(apiSide, "amount", isQuoteSize ? amountLimit("Min") : "Enter amount");
            return null;
        }

        // Leverage
        const leverage = Number.isInteger(extras.leverage) ? extras.leverage : futuresLeverage;
        const contractMaxLeverage = Number(SelectedCoin?.max_leverage) || 125;
        if (!Number.isInteger(leverage) || leverage < 1 || leverage > contractMaxLeverage) {
            alertErrorMessage(futuresErrLeverageRange(contractMaxLeverage));
            return null;
        }
        if (leverage > contractMaxLeverage) {
            alertErrorMessage(futuresErrLeverageMax(symbol, contractMaxLeverage));
            return null;
        }

        // reduce_only / close_position — mutually exclusive
        const reduceOnly     = extras.reduceOnly ?? futuresReduceOnly;
        const closePosition  = extras.closePosition ?? false;
        if (reduceOnly && closePosition) {
            alertErrorMessage(futuresErrReduceOnlyConflict());
            return null;
        }

        // TIF
        // Post Only disabled for now — always use modal TIF (no GTX from checkbox)
        // const postOnly      = extras.postOnly ?? false; // GTX
        const tifFromModal  = extras.tifValue || "GTC";
        const effectiveTif  = tifFromModal;
        // const effectiveTif  = postOnly ? "GTX" : tifFromModal;

        // IOC/FOK not with reduce_only
        if (reduceOnly && ["IOC", "FOK"].includes(effectiveTif)) {
            alertErrorMessage(futuresErrIocFokReduceOnly());
            return null;
        }
        // GTX only for LIMIT — disabled while Post Only is off
        // if (postOnly && order_type !== "LIMIT") {
        //     alertErrorMessage(futuresErrPostOnlyLimit());
        //     return null;
        // }

        // Step-size validation for quantity (base units)
        const qtyPrec = getDecimalPlaces(stepSize);
        const qtyMult = Math.pow(10, qtyPrec);
        if (Math.round(qty * qtyMult) % Math.round(stepSize * qtyMult) !== 0) {
            showFieldError(apiSide, "amount", "Invalid amount");
            return null;
        }
        if (qty < minQty) { showFieldError(apiSide, "amount", amountLimit("Min")); return null; }
        if (qty > maxQty) { showFieldError(apiSide, "amount", amountLimit("Max", maxQty)); return null; }

        // Build base payload
        const payload = {
            symbol,
            side:       apiSide,
            order_type,
            quantity:   String(formatQuantity(qty)),
            leverage,
        };

        // ── LIMIT ─────────────────────────────────────────────────────────────
        if (order_type === "LIMIT") {
            const rawPrice  = isBuy ? buyOrderPrice : sellOrderPrice;
            const fallback  = isBuy ? (Number(buyprice) || 0) : (Number(sellPrice) || 0);
            const priceVal  = String(rawPrice).trim() !== "" ? parseFloat(rawPrice) : fallback;

            if (!Number.isFinite(priceVal) || priceVal <= 0) {
                showFieldError(apiSide, "price", "Enter price");
                return null;
            }

            // Tick-size validation
            const pricePrec = getDecimalPlaces(tickSize);
            const priceMult = Math.pow(10, pricePrec);
            if (Math.round(priceVal * priceMult) % Math.round(tickSize * priceMult) !== 0) {
                showFieldError(apiSide, "price", "Invalid price");
                return null;
            }

            // Min notional
            const limitNotional = isQuoteSize ? rawVal : priceVal * qty;
            if (minNotional > 0 && limitNotional > 0 && limitNotional < minNotional) {
                showFieldError(apiSide, "amount", amountLimit("Min"));
                return null;
            }

            payload.price = String(priceVal);

            if (effectiveTif && effectiveTif !== "GTC") {
                payload.time_in_force = effectiveTif;
            }

        // ── MARKET ────────────────────────────────────────────────────────────
        } else if (order_type === "MARKET") {
            // price is FORBIDDEN for MARKET — do not include it

            const markPriceForNotional = Number(buyprice) || Number(sellPrice) || markPrice || 0;
            const notional = isQuoteSize ? rawVal : qty * markPriceForNotional;
            if (minNotional > 0 && notional > 0 && notional < minNotional) {
                showFieldError(apiSide, "amount", amountLimit("Min"));
                return null;
            }

            // Optional slippage
            if (extras.slippageEnabled && extras.slippagePct) {
                const sp = parseFloat(extras.slippagePct);
                if (Number.isFinite(sp) && sp > 0 && sp <= 100) {
                    payload.slippage = sp;
                }
            }

        // ── CONDITIONAL ───────────────────────────────────────────────────────
        } else if (order_type === "CONDITIONAL") {
            // price is FORBIDDEN; trigger_price is required
            const rawTrigger = isBuy ? buyStopPrice : sellStopPrice;
            const triggerVal = parseFloat(rawTrigger);

            if (!rawTrigger || !Number.isFinite(triggerVal) || triggerVal <= 0) {
                showFieldError(apiSide, "trigger", "Enter price");
                return null;
            }

            // Tick-size validation for trigger price
            const pricePrec = getDecimalPlaces(tickSize);
            const priceMult = Math.pow(10, pricePrec);
            if (Math.round(triggerVal * priceMult) % Math.round(tickSize * priceMult) !== 0) {
                showFieldError(apiSide, "trigger", "Invalid price");
                return null;
            }

            payload.trigger_price = String(triggerVal);

            // Optional order_price (limit execution after trigger fires; omit → MARKET)
            const rawOrderPrice = isBuy ? buyOrderAfterTrigger : sellOrderAfterTrigger;
            if (rawOrderPrice && String(rawOrderPrice).trim() !== "") {
                const orderPriceVal = parseFloat(rawOrderPrice);
                if (Number.isFinite(orderPriceVal) && orderPriceVal > 0) {
                    if (Math.round(orderPriceVal * priceMult) % Math.round(tickSize * priceMult) !== 0) {
                        showFieldError(apiSide, "orderPrice", "Invalid price");
                        return null;
                    }
                    if (minNotional > 0) {
                        const condNotional = isQuoteSize ? rawVal : orderPriceVal * qty;
                        if (condNotional > 0 && condNotional < minNotional) {
                            showFieldError(apiSide, "amount", amountLimit("Min"));
                            return null;
                        }
                    }
                    payload.order_price = String(orderPriceVal);
                }
            } else {
                // Executes as MARKET after trigger — check notional against trigger price
                const condNotional = isQuoteSize ? rawVal : triggerVal * qty;
                if (minNotional > 0 && condNotional > 0 && condNotional < minNotional) {
                    showFieldError(apiSide, "amount", amountLimit("Min"));
                    return null;
                }
            }
        }

        // ── Optional TP / SL ──────────────────────────────────────────────────
        const markPriceForTpSl = Number(buyprice) || Number(sellPrice) || 0;

        if (extras.takeProfit && String(extras.takeProfit).trim() !== "") {
            const tpVal = parseFloat(extras.takeProfit);
            if (Number.isFinite(tpVal) && tpVal > 0) {
                if (markPriceForTpSl > 0) {
                    if (apiSide === "BUY"  && tpVal <= markPriceForTpSl) { showFieldError(apiSide, "tp", "Invalid TP"); return null; }
                    if (apiSide === "SELL" && tpVal >= markPriceForTpSl) { showFieldError(apiSide, "tp", "Invalid TP"); return null; }
                }
                payload.take_profit = String(tpVal);
            }
        }

        if (extras.stopLoss && String(extras.stopLoss).trim() !== "") {
            const slVal = parseFloat(extras.stopLoss);
            if (Number.isFinite(slVal) && slVal > 0) {
                if (markPriceForTpSl > 0) {
                    if (apiSide === "BUY"  && slVal >= markPriceForTpSl) { showFieldError(apiSide, "sl", "Invalid SL"); return null; }
                    if (apiSide === "SELL" && slVal <= markPriceForTpSl) { showFieldError(apiSide, "sl", "Invalid SL"); return null; }
                }
                payload.stop_loss = String(slVal);
            }
        }

        // ── reduce_only / close_position ─────────────────────────────────────
        if (reduceOnly)    payload.reduce_only    = true;
        if (closePosition) payload.close_position = true;

        // Leverage-tier notional cap (client-side pre-check before API call)
        let orderPriceForCap = 0;
        if (order_type === "LIMIT" && payload.price) {
            orderPriceForCap = parseFloat(payload.price);
        } else if (order_type === "MARKET") {
            orderPriceForCap =
                Number(SelectedCoin?.mark_price) ||
                Number(buyprice) ||
                Number(sellPrice) ||
                0;
        } else if (order_type === "CONDITIONAL") {
            orderPriceForCap = payload.order_price
                ? parseFloat(payload.order_price)
                : parseFloat(payload.trigger_price) || 0;
        }
        if (Number.isFinite(orderPriceForCap) && orderPriceForCap > 0) {
            const orderNotional = qty * orderPriceForCap;
            const qtyCaps = {
                leverageTiers: SelectedCoin?.leverage_tiers,
                leverage,
                maxOrderQty: maxQty,
                stepSize,
            };

            // Funds first. A size the wallet cannot open never reaches the leverage-tier check.
            if (!reduceOnly && !closePosition) {
                const takerFee = resolveTakerFeeRate(SelectedCoin);
                const effAvail = Number(futuresEffectiveAvailable);
                const availableMargin = Number.isFinite(effAvail)
                    ? effAvail
                    : (Number(BuyCoinBal) || 0);
                const requiredMargin = computeFuturesOrderMargin(orderNotional, leverage, takerFee);
                if (requiredMargin > availableMargin + 1e-8) {
                    const balanceNotional = computeMaxOpenNotional(availableMargin, leverage, takerFee);
                    const maxQtyFromMargin = computeMaxOpenQtyBtc(balanceNotional, orderPriceForCap, qtyCaps);
                    showFieldError(apiSide, "amount", amountLimit("Max", maxQtyFromMargin));
                    return null;
                }
            }

            const tierMaxNotional = getMaxNotionalAtLeverage(SelectedCoin?.leverage_tiers, leverage);
            if (Number.isFinite(tierMaxNotional) && tierMaxNotional !== Infinity && orderNotional > tierMaxNotional) {
                const tierQty = computeMaxOpenQtyBtc(tierMaxNotional, orderPriceForCap, qtyCaps);
                showFieldError(apiSide, "amount", amountLimit("Max", tierQty));
                return null;
            }
        }

        return payload;
    };

    const placeFuturesOrder = async (uiSide, extras = {}) => {
        if (futuresPlacingRef.current) return;
        const payload = buildFuturesOrderPayload(uiSide, extras);
        if (!payload) return;

        futuresPlacingRef.current = true;
        setPlacingOrderSide(uiSide);
        try {
            const result = await AuthService.futuresPlaceOrder({
                ...payload,
                client_order_id: newFuturesClientOrderId(),
            });
            if (result?.success) {
                alertSuccessMessage("Order Placed Successfully!");
                const orderData = result?.data?.order ?? result?.data;
                const orderId = orderData?._id ?? orderData?.order_id;
                const alreadyOpen =
                    orderId != null &&
                    futuresOpenOrders.some((o) => String(o._id ?? o.order_id) === String(orderId));
                if (!alreadyOpen && orderData && orderId) {
                    setFuturesOpenOrders((prev) => {
                        if (prev.some((o) => String(o._id ?? o.order_id) === String(orderId))) return prev;
                        return [orderData, ...prev];
                    });
                }
                if (futuresSymbol) subscribeToFutures({ symbol: futuresSymbol });
            } else {
                const msg = result?.error?.message || result?.message || "Failed to place order";
                if (isFuturesLeverageRejectionMessage(msg)) {
                    const apiSide = String(uiSide).toUpperCase();
                    const isBuy = apiSide === "BUY";
                    const quoteSize = resolveFuturesAmountIsQuote({
                        orderType: infoPlaceOrder,
                        isBuy,
                        contractUnitId: extras.amountUnitId ?? futuresContractUnitId,
                        buyAmountDenom,
                        sellAmountDenom,
                    });
                    const lev = Number.isInteger(extras.leverage) ? extras.leverage : futuresLeverage;
                    const px = Number(SelectedCoin?.mark_price) || Number(buyprice) || Number(sellPrice) || 0;
                    const tierMax = getMaxNotionalAtLeverage(SelectedCoin?.leverage_tiers, lev);
                    const cap = Number.isFinite(tierMax) && tierMax !== Infinity ? tierMax : 0;
                    const step = Number(SelectedCoin?.step_size) || 0.001;
                    const tick = Number(SelectedCoin?.tick_size) || 0.01;
                    const baseQty = px > 0
                        ? computeMaxOpenQtyBtc(cap, px, {
                            leverageTiers: SelectedCoin?.leverage_tiers,
                            leverage: lev,
                            maxOrderQty: Number(SelectedCoin?.max_order_qty) || 1000,
                            stepSize: step,
                        })
                        : 0;
                    const unit = quoteSize
                        ? (SelectedCoin?.quote_currency || SelectedCoin?.margin_asset || "USDT")
                        : (SelectedCoin?.base_currency || "BTC");
                    const shown = quoteSize && px > 0 ? baseQty * px : baseQty;
                    showFieldError(apiSide, "amount", amountLimitLabel("Max", shown, quoteSize ? tick : step, unit));
                } else {
                    alertErrorMessage(formatFuturesApiError(msg));
                }
            }
        } catch {
            alertErrorMessage(futuresErrGeneric());
        } finally {
            futuresPlacingRef.current = false;
            setPlacingOrderSide("");
        }
    };

    const cancelFuturesOrder = async (orderId) => {
        try {
            const result = await AuthService.futuresCancelOrder(orderId);
            if (result?.success) {
                alertSuccessMessage("Order Cancelled");
                if (futuresSymbol) subscribeToFutures({ symbol: futuresSymbol });
            } else {
                alertErrorMessage(result?.error?.message || result?.message || "Failed to cancel order");
            }
        } catch {
            alertErrorMessage("Something went wrong");
        }
    };

    /** Close an open futures position via reduce-only LIMIT or MARKET order. */
    const closeFuturesPosition = async (pos, { orderType = "MARKET", price, quantity } = {}) => {
        if (!token) return;
        if (futuresPlacingRef.current) return;
        const symbol = pos?.symbol;
        if (!symbol) {
            alertErrorMessage(futuresErrInvalidPosition());
            return;
        }

        const decVal = (v) =>
            v && typeof v === "object" && "$numberDecimal" in v ? v.$numberDecimal : v;

        const posSide = String(pos.side ?? "").toUpperCase();
        const closeSide = posSide === "SHORT" ? "BUY" : "SELL";
        const fullQty = parseFloat(decVal(pos.quantity));
        if (!Number.isFinite(fullQty) || fullQty <= 0) {
            return { ok: false, field: "amount", text: "Enter amount" };
        }

        const closeBaseCc = pos?.base_currency || SelectedCoin?.base_currency || "BTC";
        const contract =
            findFuturesContractInPairs(AllData?.pairs, symbol) ||
            (symbol === SelectedCoin?.symbol ? SelectedCoin : null) ||
            {
                tick_size: 0.01,
                step_size: 0.001,
                min_notional: 10,
                quote_currency: "USDT",
                base_currency: closeBaseCc,
            };

        const tickSize = Number(resolveIncrementSize(null, contract, "tick_size", "0.01")) || 0.01;
        const stepSize = Number(resolveIncrementSize(null, contract, "step_size", "0.001")) || 0.001;

        const rawQty =
            quantity != null && quantity !== "" ? String(quantity) : String(fullQty);
        const snappedQtyStr =
            snapAndCapCloseQty(rawQty, stepSize, fullQty) ||
            snapAndCapCloseQty(String(fullQty), stepSize, fullQty);
        const qty = futuresDecNum(snappedQtyStr);
        if (!Number.isFinite(qty) || qty <= 0) {
            return { ok: false, field: "amount", text: "Enter amount" };
        }
        if (qty > fullQty + 1e-12) {
            return { ok: false, field: "amount", text: amountLimitLabel("Max", fullQty, stepSize, closeBaseCc) };
        }
        const qtyPrec = getDecimalPlaces(stepSize);
        const qtyMult = Math.pow(10, qtyPrec);
        if (Math.round(qty * qtyMult) % Math.round(stepSize * qtyMult) !== 0) {
            return { ok: false, field: "amount", text: "Invalid amount" };
        }

        const leverage = Number(pos.leverage) || futuresLeverage || 1;
        // Full close (100% / qty == position size): close_position, not reduce_only.
        // Partial close: send entered size with reduce_only.
        const isFullClose = qty >= fullQty - 1e-12;
        const payload = {
            symbol,
            side: closeSide,
            order_type: orderType,
            quantity: String(qty),
            leverage,
            ...(isFullClose ? { close_position: true } : { reduce_only: true }),
        };

        if (orderType === "LIMIT") {
            const snappedPriceStr = snapToIncrementInput(String(price ?? ""), tickSize);
            const priceVal = futuresDecNum(snappedPriceStr);
            if (!Number.isFinite(priceVal) || priceVal <= 0) {
                return { ok: false, field: "price", text: "Enter price" };
            }
            const pricePrec = getDecimalPlaces(tickSize);
            const priceMult = Math.pow(10, pricePrec);
            if (Math.round(priceVal * priceMult) % Math.round(tickSize * priceMult) !== 0) {
                return { ok: false, field: "price", text: "Invalid price" };
            }
            const minNotional = Number(contract.min_notional) || 0;
            if (minNotional > 0 && priceVal * qty < minNotional) {
                const minBase = priceVal > 0 ? minNotional / priceVal : minNotional;
                return { ok: false, field: "amount", text: amountLimitLabel("Min", minBase, stepSize, closeBaseCc) };
            }
            payload.price = String(priceVal);
        } else if (orderType === "MARKET") {
            const markPx =
                parseFloat(decVal(pos.mark_price)) ||
                Number(buyprice) ||
                Number(sellPrice) ||
                0;
            const minNotional = Number(contract.min_notional) || 0;
            if (minNotional > 0 && markPx > 0 && markPx * qty < minNotional) {
                const minBase = markPx > 0 ? minNotional / markPx : minNotional;
                return { ok: false, field: "amount", text: amountLimitLabel("Min", minBase, stepSize, closeBaseCc) };
            }
        }

        futuresPlacingRef.current = true;
        try {
            const result = await AuthService.futuresPlaceOrder({
                ...payload,
                client_order_id: newFuturesClientOrderId(),
            });
            if (result?.success) {
                alertSuccessMessage(orderType === "MARKET" ? "Position close order placed" : "Limit close order placed");
                if (futuresSymbol) subscribeToFutures({ symbol: futuresSymbol });
                return true;
            } else {
                const msg = result?.error?.message || result?.message || "Failed to close position";
                if (isFuturesLeverageRejectionMessage(msg)) {
                    setFuturesOrderRejectionOpen(true);
                } else {
                    alertErrorMessage(formatFuturesApiError(msg));
                }
                return false;
            }
        } catch {
            alertErrorMessage(futuresErrGeneric());
            return false;
        } finally {
            futuresPlacingRef.current = false;
        }
    };

    const fetchFuturesOrderHistory = useCallback(async () => {
        if (!token) return;
        try {
            // Single endpoint returns all terminal statuses (FILLED, CANCELLED, REJECTED, EXPIRED)
            const params = { skip: 0, limit: 50, symbol: "all" };
            const result = await AuthService.futuresOrderHistory(params);
            if (result?.success) {
                setFuturesOrderHistory(result.data?.orders ?? []);
            }
        } catch { /* keep existing */ }
    }, [token]);

    const fetchFuturesPositionHistory = useCallback(async () => {
        if (!token) return;
        try {
            // Single endpoint returns both CLOSED and LIQUIDATED
            const params = { skip: 0, limit: 50, symbol: "all" };
            const result = await AuthService.futuresPositionHistory(params);
            if (result?.success) {
                setFuturesPositionHistory(result.data?.positions ?? []);
            }
        } catch { /* keep existing */ }
    }, [token]);

    const fetchFuturesTradeHistory = useCallback(async () => {
        if (!token) return;
        try {
            const params = { skip: 0, limit: 50, symbol: "all" };
            const result = await AuthService.futuresExecutions(params);
            if (result?.success) {
                // Response key is "trades" (not "executions")
                setFuturesTradeHistory(result.data?.trades ?? []);
            }
        } catch { /* keep existing */ }
    }, [token]);

    const fetchFuturesWalletHistory = useCallback(async () => {
        if (!token) return;
        try {
            const f = walletHistoryFilters;
            const params = {
                page: f.page,
                limit: f.limit,
            };
            if (f.asset)  params.asset  = f.asset;
            if (f.symbol) params.symbol = f.symbol;
            if (f.type)   params.type   = f.type;
            if (f.from)   params.from   = `${f.from}T00:00:00.000Z`;
            if (f.to)     params.to     = `${f.to}T23:59:59.999Z`;
            const result = await AuthService.futuresWalletHistory(params);
            if (result?.success) {
                setFuturesWalletHistory(result.data?.transactions ?? []);
                setFuturesWalletHistoryPagination(result.data?.pagination ?? {
                    total: 0, page: 1, limit: f.limit, pages: 1,
                });
            }
        } catch { /* keep existing */ }
    }, [token, walletHistoryFilters]);

    // Open orders + positions + balance: socket only — no REST interval poll

    // Poll the relevant futures history endpoint every 3s while its tab is open
    // (all pairs via symbol=all — not scoped to the selected contract).
    useEffect(() => {
        if (!token) return undefined;
        let fetcher = null;
        if (positionOrderTab === "orderHistory")    fetcher = fetchFuturesOrderHistory;
        if (positionOrderTab === "positionHistory") fetcher = fetchFuturesPositionHistory;
        if (positionOrderTab === "tradeHistory")    fetcher = fetchFuturesTradeHistory;
        if (!fetcher) return undefined;
        void fetcher();
        const id = window.setInterval(() => void fetcher(), 3000);
        return () => window.clearInterval(id);
    }, [token, positionOrderTab, fetchFuturesOrderHistory, fetchFuturesPositionHistory, fetchFuturesTradeHistory]);

    // Wallet transaction history — fetch once when tab opens or filters change (no polling)
    useEffect(() => {
        if (!token || positionOrderTab !== "transactionHistory") return;
        void fetchFuturesWalletHistory();
    }, [token, positionOrderTab, fetchFuturesWalletHistory]);

    const handleAddFav = async (pairId) => {
        LoaderHelper.loaderStatus(true);
        await AuthService.favoriteCoin(pairId).then((result) => {
            if (result?.success) {
                LoaderHelper.loaderStatus(false);
                favoriteList();
            } else {
                LoaderHelper.loaderStatus(false);
                alertErrorMessage(result?.message);
            }
        });
    };
    const favoriteList = async () => {
        await AuthService.favoriteList().then((result) => {
            if (result?.success) {
                setfavCoins(result?.data?.pairs ? result?.data?.pairs : ['']);
            }
        });
    };

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        if (token) {
            favoriteList();
            fetchSpotWallets();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);


    // Reset orderbook when switching pairs
    const resetOrderbook = () => {
        // Flip the loading flag back FIRST so the next render shows the spinner
        // instead of an empty "No Orders" frame (the pair-change useEffect would
        // otherwise only catch up one render later).
        setOrderBookSocketReady(false);
        setSellOrders([]);
        setBuyOrders([]);
        setRecentTrade([]);
        setOrderBookViewMode("both");
        setOrderBookSwapAmountTotal(false);
    };

    const handleSelectCoin = (data) => {
        // Skip if selecting the same pair - don't reset or re-subscribe
        if (SelectedCoin?.base_currency_id === data?.base_currency_id &&
            SelectedCoin?.quote_currency_id === data?.quote_currency_id) {
            setShowCoinList(false);
            return;
        }

        resetOrderbook();
        setinfoPlaceOrder("LIMIT");
        navigate(`/futures_trade/${data?.base_currency}_${data?.quote_currency}`);
        limitPriceSeededPairRef.current = `${data?.base_currency_id}|${data?.quote_currency_id}`;
        setSelectedCoin(data);
        setbaseCurId(data?.base_currency_id);
        setquoteCurId(data?.quote_currency_id);
        setbuyprice(data?.buy_price);
        setsellPrice(data?.sell_price);
        seedLimitPrice(data?.buy_price ?? data?.sell_price);
        lastStreamBidRef.current = null;
        setlastMarketPrice(data?.buy_price);
        setShowCoinList(false);
        setbuyamount("");
        setsellAmount("");
        setBuyStopPrice("");
        setSellStopPrice("");
        setBuyOrderAfterTrigger("");
        setSellOrderAfterTrigger("");
        setStopBuyPercent(0);
        setStopSellPercent(0);
        setLimitBuyPercent(0);
        setLimitSellPercent(0);
        setMarketBuyPercent(0);
        setMarketSellPercent(0);
        setbuyAmountDenom("BASE");
        setsellAmountDenom("BASE");
        setFuturesContractUnitId("amount_base");
        setExpandedRowIndex(null);
        let meta = Coins?.find(
            (item) => item?.short_name === data?.base_currency || item?.base_currency === data?.base_currency
        );
        setDesAndLinks({ ...(meta || {}), description: meta?.description || "", links: meta?.links || [] });
    };

    const [desAndLinks, setDesAndLinks] = useState({ description: "", links: [] });
    const getDescAndLink = () => {
        if (SelectedCoin) {
            let meta = Coins?.find(
                (item) => item?.short_name === SelectedCoin?.base_currency || item?.base_currency === SelectedCoin?.base_currency
            );
            setDesAndLinks({ ...(meta || {}), description: meta?.description || "", links: meta?.links || [] });
        }
    }

    const nineDecimalFormat = (data) => {
        if (typeof (data) === "number") {
            // return data
            return parseFloat(data?.toFixed(9))
        } else {
            return 0
        }
    };

    // Get decimal places from tick_size or step_size
    const getDecimalPlaces = (value) => {
        if (!value || value >= 1) return 0;
        const str = value.toString();
        if (str.includes('e-')) {
            return parseInt(str.split('e-')[1]);
        }
        const decimalPart = str.split('.')[1];
        return decimalPart ? decimalPart.length : 0;
    };

    // Get price precision based on tick_size
    const getPricePrecision = () => {
        const tickSize = SelectedCoin?.tick_size;
        if (tickSize === undefined || tickSize === null) return 8; // default if no tick_size
        return getDecimalPlaces(tickSize);
    };

    // Get quantity precision based on step_size
    const getQuantityPrecision = () => {
        const stepSize = SelectedCoin?.step_size;
        if (stepSize === undefined || stepSize === null) return 8; // default if no step_size
        return getDecimalPlaces(stepSize);
    };

    // Format price based on tick_size precision
    const formatPrice = (price) => {
        if (price === undefined || price === null || isNaN(price)) return '0';
        const precision = getPricePrecision();
        return parseFloat(Number(price).toFixed(precision));
    };

    /** Set limit price once (long + short fields stay in sync). */
    const seedLimitPrice = (price) => {
        const formatted = formatPrice(price);
        const n = Number(formatted);
        if (!Number.isFinite(n) || n <= 0) return;
        const str = String(formatted);
        setbuyOrderPrice(str);
        setsellOrderPrice(str);
    };

    const setLimitPrice = (price) => {
        seedLimitPrice(price);
    };

    /** Order book row click — fill limit price only; keep user's amount unchanged. */
    const applyOrderBookPrice = (price) => {
        if (infoPlaceOrder !== "MARKET") {
            setLimitPrice(price);
        }
    };

    const resetOrderAmountSliders = () => {
        setLimitBuyPercent(0);
        setLimitSellPercent(0);
        setMarketBuyPercent(0);
        setMarketSellPercent(0);
        setStopBuyPercent(0);
        setStopSellPercent(0);
    };

    const pairKeyForLimit = baseCurId && quoteCurId ? `${baseCurId}|${quoteCurId}` : null;

    // Seed limit price on first mark for the pair (socket); pair select seeds separately.
    useEffect(() => {
        if (!pairKeyForLimit || limitPriceSeededPairRef.current === pairKeyForLimit) return;
        const p = Number(buyprice);
        if (!Number.isFinite(p) || p <= 0) return;
        limitPriceSeededPairRef.current = pairKeyForLimit;
        seedLimitPrice(p);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pairKeyForLimit, buyprice]);

    // Format quantity based on step_size precision
    const formatQuantity = (qty) => {
        if (qty === undefined || qty === null || isNaN(qty)) return '0';
        const precision = getQuantityPrecision();
        return parseFloat(Number(qty).toFixed(precision));
    };

    // Validate order before placing
    const validateOrder = (price, quantity, side, amountIsQuote = false, orderKind) => {
        const tick_size = SelectedCoin?.tick_size || 0.01;
        const step_size = SelectedCoin?.step_size || 0.00001;
        const min_notional = SelectedCoin?.min_notional || 5;
        const max_order_qty = SelectedCoin?.max_order_qty || 9000;

        const skipPriceTick = orderKind === "MARKET" || orderKind === "STOP_MARKET";

        const numPrice = parseFloat(price);
        const numQuantity = parseFloat(quantity);

        if (!Number.isFinite(numPrice) || !Number.isFinite(numQuantity)) {
            alertErrorMessage("Invalid price or amount");
            return false;
        }

        const baseQty = amountIsQuote && numPrice > 0 ? numQuantity / numPrice : numQuantity;
        const total = amountIsQuote ? numQuantity : numPrice * numQuantity;

        // Validate price tick_size (limit / stop-limit; market uses server price)
        if (!skipPriceTick) {
            const pricePrecision = getDecimalPlaces(tick_size);
            const priceMultiplier = Math.pow(10, pricePrecision);
            if (Math.round(numPrice * priceMultiplier) % Math.round(tick_size * priceMultiplier) !== 0) {
                alertErrorMessage(`Price must be a multiple of ${tick_size}`);
                return false;
            }
        }

        if (amountIsQuote && numPrice <= 0) {
            alertErrorMessage("Price is required to size this order");
            return false;
        }

        // Validate quantity step_size (base units)
        const qtyPrecision = getDecimalPlaces(step_size);
        const qtyMultiplier = Math.pow(10, qtyPrecision);
        if (Math.round(baseQty * qtyMultiplier) % Math.round(step_size * qtyMultiplier) !== 0) {
            alertErrorMessage(`Quantity must be a multiple of ${step_size}`);
            return false;
        }

        // Validate max_order_qty (base)
        if (baseQty > max_order_qty) {
            alertErrorMessage(`Maximum order quantity is ${max_order_qty} ${SelectedCoin?.base_currency}`);
            return false;
        }

        // Validate min_notional (minimum order value in quote)
        if (total < min_notional) {
            alertErrorMessage(`Minimum order value is ${min_notional} ${SelectedCoin?.quote_currency}`);
            return false;
        }

        // Validate insufficient funds
        if (side === "BUY") {
            const availableBalance = BuyCoinBal || 0;
            const spend = amountIsQuote ? numQuantity : total;
            if (spend > availableBalance) {
                alertErrorMessage(`Insufficient funds`);
                return false;
            }
        } else if (side === "SELL") {
            const availableBalance = SellCoinBal || 0;
            if (baseQty > availableBalance) {
                alertErrorMessage(`Insufficient funds`);
                return false;
            }
        }

        return true;
    };

    const formatTotal = (value) => {
        const precision = getPricePrecision();
        const finalValue = value?.toFixed(precision)?.replace(/\.?0+$/, '');
        let formattedNum = finalValue?.toString();
        let result = formattedNum?.replace(/^0\.0*/, '');
        const decimalPart = finalValue?.toString()?.split('.')[1];
        if (!decimalPart) return finalValue;
        let zeroCount = 0;
        for (let char of decimalPart) {
            if (char === '0') {
                zeroCount++;
            } else {
                break;
            }
        }
        if (zeroCount > 4) {
            return `0.0{${zeroCount}}${result}`;
        }
        if (value < 1e-7) {
            return `0.0{${zeroCount}}${result}`;
        } else {
            return finalValue;
        }
    };

    const toFixed8 = (data) => {
        const precision = getQuantityPrecision();
        const multiplier = Math.pow(10, precision);
        return Math.floor(data * multiplier) / multiplier;
    };

    const applySpotOrderType = (next) => {
        setinfoPlaceOrder(next);
        setOrderFieldError(null);
        if (next === "MARKET" || next === "STOP_MARKET") {
            setbuyOrderPrice("");
            setsellOrderPrice("");
            return;
        }
        if (next === "LIMIT" || next === "STOP_LIMIT") {
            const live = Number(lastMarketPrice) || Number(buyprice) || Number(sellPrice) || 0;
            seedLimitPrice(live);
        }
    };

    const parseOrderQty = (s) => {
        const n = parseFloat(String(s ?? "").replace(/,/g, ""));
        return Number.isFinite(n) ? n : NaN;
    };

    const buildSpotOrderToApi = (side) => {
        const t = infoPlaceOrder;
        const isMarketT = t === "MARKET" || t === "STOP_MARKET";
        const isQuoteSize =
            (t === "MARKET" || t === "STOP_MARKET" || t === "STOP_LIMIT" || t === "CONDITIONAL") &&
            (side === "BUY" ? buyAmountDenom === "QUOTE" : sellAmountDenom === "QUOTE");
        const raw = side === "BUY" ? buyamount : sellAmount;

        const refForMarket = side === "BUY" ? (Number(buyprice) || 0) : (Number(sellPrice) || 0);
        const limitFromUi =
            side === "BUY"
                ? (String(buyOrderPrice).trim() !== "" ? parseFloat(buyOrderPrice) : NaN) || (Number(buyprice) || 0)
                : (String(sellOrderPrice).trim() !== "" ? parseFloat(sellOrderPrice) : NaN) || (Number(sellPrice) || 0);

        const priceForValidation = isMarketT ? refForMarket : limitFromUi;

        const refForStopDefault = refForMarket;
        const stopPx =
            t === "STOP_LIMIT" || t === "STOP_MARKET" || t === "CONDITIONAL"
                ? (side === "BUY"
                    ? (String(buyStopPrice).trim() !== "" ? parseFloat(buyStopPrice) : refForStopDefault)
                    : (String(sellStopPrice).trim() !== "" ? parseFloat(sellStopPrice) : refForStopDefault))
                : null;

        const priceForApi = isMarketT ? refForMarket : limitFromUi;

        const baseQty = (() => {
            const n = parseOrderQty(raw);
            if (!Number.isFinite(n) || n <= 0) return NaN;
            if (isQuoteSize && priceForValidation > 0) {
                return toFixed8(n / priceForValidation);
            }
            return toFixed8(n);
        })();

        const extras = {};
        if (stopPx != null && Number.isFinite(stopPx) && (t === "STOP_LIMIT" || t === "STOP_MARKET" || t === "CONDITIONAL")) {
            extras.stop_price = stopPx;
        }
        if (t === "LIMIT" || t === "STOP_LIMIT" || t === "CONDITIONAL") {
            if (side === "BUY") {
                if (limitBuyFok) extras.fok = true;
                if (limitBuyIoc) extras.ioc = true;
            } else {
                if (limitSellFok) extras.fok = true;
                if (limitSellIoc) extras.ioc = true;
            }
        }
        return { t, priceForValidation, priceForApi, baseQty, raw, isQuoteSize, extras };
    };

    /**
     * Build a Spot Trading `/spot/v1/orders` payload from the current UI state.
     * Follows the Spot Trading Frontend Integration Guide:
     *   - `pair` is the uppercase engine symbol (e.g. "BTCUSDT").
     *   - `price` is only sent for LIMIT / STOP_LIMIT.
     *   - `stop_price` is only sent for STOP_LIMIT / STOP_MARKET.
     *   - FOK / IOC checkboxes map to `time_in_force`; default is `GTC`.
     *   - Decimal fields are sent as strings to avoid float precision loss.
     */
    const buildSpotOrderPayload = (side, t, priceForApi, baseQty, extras) => {
        const base = String(SelectedCoin?.base_currency || "").toUpperCase();
        const quote = String(SelectedCoin?.quote_currency || "").toUpperCase();
        const pair = base && quote ? `${base}${quote}` : "";

        const payload = {
            pair,
            type: t,
            side,
            quantity: String(baseQty),
        };

        if (t === "LIMIT" || t === "STOP_LIMIT" || t === "CONDITIONAL") {
            payload.price = String(priceForApi);
        }
        if ((t === "STOP_LIMIT" || t === "STOP_MARKET" || t === "CONDITIONAL") && extras?.stop_price != null) {
            payload.stop_price = String(extras.stop_price);
        }

        let tif = "GTC";
        if (t === "LIMIT" || t === "STOP_LIMIT" || t === "CONDITIONAL") {
            if (extras?.fok) tif = "FOK";
            else if (extras?.ioc) tif = "IOC";
        }
        if (tif !== "GTC") {
            payload.time_in_force = tif;
        }

        return payload;
    };

    // eslint-disable-next-line no-unused-vars
    const placeSpotOrder = async (side) => {
        if (!SelectedCoin?.base_currency_id || !SelectedCoin?.quote_currency_id) {
            alertErrorMessage("Select a trading pair");
            return;
        }
        if (!MARGIN_SPOT_API_ORDER_KINDS.has(infoPlaceOrder)) {
            alertErrorMessage(
                "This margin order mode is UI-only until the API supports it. Use Limit, Market, Stop Limit, or Stop Market to place an order."
            );
            return;
        }
        const { t, priceForValidation, priceForApi, baseQty, raw, isQuoteSize, extras } = buildSpotOrderToApi(side);
        if (!Number.isFinite(baseQty) || baseQty <= 0) {
            alertErrorMessage("Enter a valid amount");
            return;
        }
        if (!validateOrder(priceForValidation, String(raw), side, isQuoteSize, t)) {
            return;
        }
        const payload = buildSpotOrderPayload(side, t, priceForApi, baseQty, extras);
        if (!payload.pair) {
            alertErrorMessage("Select a trading pair");
            return;
        }
        setPlacingOrderSide(side);
        try {
            const result = await AuthService.placeOrder(payload);
            if (result?.success) {
                try {
                    alertSuccessMessage("Order Placed Successfully!!");
                    if (futuresSymbol) subscribeToFutures({ symbol: futuresSymbol });
                } catch {
                    // Silent fail
                }
            } else {
                alertErrorMessage(result?.message);
            }
        } catch {
            alertErrorMessage("Something went wrong. Please try again.");
        } finally {
            setPlacingOrderSide("");
        }
    };

    // Check if a value being typed could lead to a valid price >= tick_size
    const isValidPriceInput = (value) => {
        const valueClean = String(value).replace(/,/g, '');
        if (valueClean === '' || valueClean === '0') return true;
        const tickSize = SelectedCoin?.tick_size || 0.01;
        const pricePrecision = getPricePrecision();

        // Check decimal precision
        const regex = new RegExp(`^\\d*\\.?\\d{0,${pricePrecision}}$`);
        if (!regex.test(valueClean)) return false;

        // If it ends with a dot, allow it (user is still typing)
        if (valueClean.endsWith('.')) return true;

        const numValue = parseFloat(valueClean);
        if (isNaN(numValue)) return false;

        // If value is 0, allow (will be validated on submit)
        if (numValue === 0) return true;

        // Value must be >= tick_size
        return numValue >= tickSize;
    };

    // Check if a value being typed could lead to a valid quantity
    const isValidQuantityInput = (value, { isQuoteAmount = false } = {}) => {
        if (value === '' || value === '0') return true;
        const quoteDec = SelectedCoin?.quote_decimal ?? 2;
        const qtyPrecision = isQuoteAmount ? quoteDec : getQuantityPrecision();

        const regex = new RegExp(`^\\d*\\.?\\d{0,${qtyPrecision}}$`);
        if (!regex.test(value)) return false;

        if (value.endsWith('.')) return true;

        const numValue = parseFloat(value);
        if (isNaN(numValue)) return false;

        // Allow partial values while typing; min notional / step size enforced on blur + submit.
        return numValue >= 0;
    };

    // Handle price input - sync buy and sell states if applicable
    const handlePriceInput = (value, setter) => {
        const stripped = String(value).replace(/,/g, '');
        if (isValidPriceInput(stripped)) {
            if (setter === setbuyOrderPrice || setter === setsellOrderPrice) {
                setbuyOrderPrice(stripped);
                setsellOrderPrice(stripped);
            } else {
                setter(stripped);
            }
        }
    };

    const setSharedOrderAmount = (value) => {
        setbuyamount(value);
        setsellAmount(value);
    };

    // Handle quantity input - sync buy and sell states if applicable
    const handleQuantityInput = (value, setter, { isQuoteAmount = false } = {}) => {
        if (isValidQuantityInput(value, { isQuoteAmount })) {
            if (setter === setbuyamount || setter === setsellAmount) {
                resetOrderAmountSliders();
                setSharedOrderAmount(value);
            } else {
                setter(value);
            }
        }
    };

    // Round value to nearest step/tick on blur (cleanup)
    const handlePriceBlur = (value, setter) => {
        const v = String(value).replace(/,/g, '');
        if (v === '' || v === '0' || v === '0.') {
            setter('');
            return;
        }
        const tickSize = SelectedCoin?.tick_size || 0.01;
        const numValue = parseFloat(v);
        if (isNaN(numValue) || numValue === 0) {
            setter('');
            return;
        }
        // Ensure minimum tick_size
        if (numValue < tickSize) {
            setter(tickSize.toString());
            return;
        }
        // Round to nearest tick_size
        const rounded = Math.round(numValue / tickSize) * tickSize;
        const precision = getPricePrecision();
        setter(parseFloat(rounded.toFixed(precision)).toString());
    };

    const handleQuantityBlur = (value, setter, { isQuoteAmount = false } = {}) => {
        const applyAmount = (v) => {
            if (setter === setbuyamount || setter === setsellAmount) {
                resetOrderAmountSliders();
                setSharedOrderAmount(v);
            } else {
                setter(v);
            }
        };

        if (value === '' || value === '0' || value === '0.') {
            applyAmount('');
            return;
        }
        const numValue = parseFloat(String(value).replace(/,/g, ""));
        if (isNaN(numValue) || numValue === 0) {
            applyAmount('');
            return;
        }

        if (isQuoteAmount) {
            const quoteDec = SelectedCoin?.quote_decimal ?? 2;
            applyAmount(parseFloat(numValue.toFixed(quoteDec)).toString());
            return;
        }

        const stepSize = Number(SelectedCoin?.step_size) || 0.00001;
        const minQty = Number(SelectedCoin?.min_order_qty) || stepSize;
        const precision = getQuantityPrecision();
        if (numValue < minQty) {
            applyAmount(parseFloat(numValue.toFixed(precision)).toString());
            return;
        }
        const rounded = Math.round(numValue / stepSize) * stepSize;
        const snapped = rounded > 0 ? rounded : numValue;
        applyAmount(parseFloat(snapped.toFixed(precision)).toString());
    };

    const displaySellOrders = useMemo(() => {
        const rows = aggregateOrderBookRows(SellOrders, orderBookAggStep);
        const sorted = rows.sort((a, b) => b.price - a.price);
        return orderBookViewMode === "both" ? sorted.slice(-ORDER_BOOK_BOTH_DEPTH) : sorted;
    }, [SellOrders, orderBookAggStep, orderBookViewMode]);

    const displayBuyOrders = useMemo(() => {
        const rows = aggregateOrderBookRows(BuyOrders, orderBookAggStep);
        const sorted = rows.sort((a, b) => b.price - a.price);
        return orderBookViewMode === "both" ? sorted.slice(0, ORDER_BOOK_BOTH_DEPTH) : sorted;
    }, [BuyOrders, orderBookAggStep, orderBookViewMode]);

    /** Last price for header + order book mid: stream when connected, else pair snapshot. */
    const displayLastForHeader = useMemo(() => {
        return lastMarketPrice !== "" && lastMarketPrice != null ? lastMarketPrice : buyprice;
    }, [lastMarketPrice, buyprice]);

    const markPriceForMax = useMemo(() => {
        const n = Number(displayLastForHeader ?? buyprice);
        return Number.isFinite(n) && n > 0 ? n : 0;
    }, [displayLastForHeader, buyprice]);

    const contractMarkPrice = useMemo(() => {
        const m = Number(SelectedCoin?.mark_price);
        if (Number.isFinite(m) && m > 0) return m;
        return markPriceForMax;
    }, [SelectedCoin?.mark_price, markPriceForMax]);

    const maxOpenNotional = useMemo(() => {
        const avail = Number(futuresEffectiveAvailable);
        if (!Number.isFinite(avail)) return 0;
        return computeMaxOpenNotional(avail, futuresLeverage, resolveTakerFeeRate(SelectedCoin));
    }, [futuresEffectiveAvailable, futuresLeverage, SelectedCoin?.taker_fee_rate, SelectedCoin?.taker_fee]);

    const maxQtyCaps = useMemo(
        () => ({
            leverageTiers: SelectedCoin?.leverage_tiers ?? [],
            leverage: futuresLeverage,
            maxOrderQty: SelectedCoin?.max_order_qty,
            stepSize: SelectedCoin?.step_size,
        }),
        [SelectedCoin, futuresLeverage]
    );

    const maxBuyVolume = useMemo(
        () => computeMaxOpenQtyBtc(maxOpenNotional, contractMarkPrice, maxQtyCaps),
        [maxOpenNotional, contractMarkPrice, maxQtyCaps]
    );

    const maxSellVolume = maxBuyVolume;

    /** Depth-bar scale — largest visible book level (same as spot), not max-open qty. */
    const orderBookMaxSellVolume = useMemo(
        () => Math.max(...displaySellOrders.map((order) => Number(order.remaining ?? order.quantity) || 0), 0),
        [displaySellOrders]
    );
    const orderBookMaxBuyVolume = useMemo(
        () => Math.max(...displayBuyOrders.map((order) => Number(order.remaining ?? order.quantity) || 0), 0),
        [displayBuyOrders]
    );

    const orderBookBidAskRatio = useMemo(() => {
        const bid = displayBuyOrders.reduce((s, o) => s + (Number(o.remaining ?? o.quantity) || 0), 0);
        const ask = displaySellOrders.reduce((s, o) => s + (Number(o.remaining ?? o.quantity) || 0), 0);
        const t = bid + ask;
        if (t <= 0) return { bidPct: 50, askPct: 50 };
        return { bidPct: (bid / t) * 100, askPct: (ask / t) * 100 };
    }, [displayBuyOrders, displaySellOrders]);

    /** Only auto-scroll to best ask on first data for a pair, or when base/quote changes (not on every book update). */
    const lastAskScrollPairKeyRef = useRef(null);
    const askScrollPairKey = baseCurId && quoteCurId ? `${baseCurId}|${quoteCurId}` : null;

    useEffect(() => {
        if (!askScrollPairKey) return;
        if (marginTpSlPairKeySeenRef.current === askScrollPairKey) return;
        marginTpSlPairKeySeenRef.current = askScrollPairKey;
        setMarginFormTpSl({
            tpTriggerPrice: "",
            tpLimitMarket: true,
            tpLimitPrice: "",
            slTriggerPrice: "",
            slLimitMarket: true,
            slLimitPrice: "",
            quantity: "",
            amountPercent: 0,
        });
        setMarginFormTwap({
            totalQty: "",
            totalPercent: 0,
            durationHours: "",
            durationMinutes: "",
            priceVariance: "",
            priceVarianceUnit: "%",
            activationPrice: "",
            timeInterval: "",
            timeIntervalUnit: "Hour",
            perSubOrderQty: "",
        });
        setMarginFormIcebergPro({
            priceVariance: "",
            priceVarianceUnit: "%",
            activationPrice: "",
            totalQty: "",
            perSubOrderQty: "",
        });
        setMarginFormLoopOrder({
            price: "",
            priceMode: "LAST_PRICE",
            takeProfitPct: "",
            stopLossPct: "",
            pauseAfterSlMin: "",
            amount: "",
            totalLoops: "",
            validFor: "1Day",
        });
        setMarginFormTrailingStop({
            pullbackRatePct: "",
            pullbackUnit: "%",
            activationPrice: "",
            amount: "",
            amountPercent: 0,
            validFor: "1Day",
        });
        setMarginFormConditional({
            triggerPrice: "",
            orderMarket: true,
            orderLimitPrice: "",
            amount: "",
            amountUnitId: "amount_base",
            amountPercent: 0,
        });
    }, [askScrollPairKey]);

    useEffect(() => {
        if (infoPlaceOrder !== "TP_SL") return;
        const raw = displayLastForHeader;
        if (raw === "" || raw == null) return;
        const num = Number(String(raw).replace(/,/g, ""));
        if (!Number.isFinite(num) || num === 0) return;
        const s = String(raw).replace(/,/g, "");
        setMarginFormTpSl((prev) => {
            const next = { ...prev };
            let changed = false;
            if (prev.tpTriggerPrice === "") {
                next.tpTriggerPrice = s;
                changed = true;
            }
            if (prev.slTriggerPrice === "") {
                next.slTriggerPrice = s;
                changed = true;
            }
            if (!prev.tpLimitMarket && prev.tpLimitPrice === "") {
                next.tpLimitPrice = s;
                changed = true;
            }
            if (!prev.slLimitMarket && prev.slLimitPrice === "") {
                next.slLimitPrice = s;
                changed = true;
            }
            return changed ? next : prev;
        });
    }, [infoPlaceOrder, displayLastForHeader]);

    // Sell/ask list: sorted high → low, so best (lowest) ask is the last row — pin scroll to bottom
    useLayoutEffect(() => {
        if (!orderBookSocketReady) return;
        if (orderBookViewMode === "bids") return;
        if (!askScrollPairKey) return;
        if (!displaySellOrders?.length) return;
        if (lastAskScrollPairKeyRef.current === askScrollPairKey) return;
        const run = () => {
            const nodes = document.querySelectorAll(".orderbook-asks-sell-list");
            if (nodes.length === 0) return;
            nodes.forEach((el) => {
                const top = el.scrollHeight - el.clientHeight;
                if (top > 0) el.scrollTop = top;
            });
            lastAskScrollPairKeyRef.current = askScrollPairKey;
        };
        requestAnimationFrame(() => requestAnimationFrame(run));
    }, [askScrollPairKey, displaySellOrders?.length, orderBookViewMode, orderBookSocketReady]);

    const formatOrderBookAggPrice = (price) => {
        if (price === undefined || price === null || Number.isNaN(Number(price))) return "0";
        const n = Number(price);
        const step = Number(orderBookAggStep);
        if (Number.isFinite(step) && step >= 1) return String(Math.round(n));
        const dp = step > 0 && step < 1 ? Math.min(8, getDecimalPlaces(step) + 1) : 1;
        return parseFloat(n.toFixed(dp)).toString();
    };

    const formatPriceThousands = (raw) => {
        const n = parseFloat(String(raw).replace(/,/g, ""));
        if (Number.isNaN(n)) return raw === undefined || raw === null ? "" : String(raw);
        const prec = getPricePrecision();
        return n.toLocaleString("en-US", { maximumFractionDigits: prec, minimumFractionDigits: 0 });
    };

    const clampSliderPct = (pct) => ([0, 25, 50, 75, 100].includes(pct) ? pct : 0);

    /** Slider amount from % of max open — base qty (Amount) or notional USDT (Value / QUOTE). */
    const amountAtMaxPct = (maxQty, maxNotional, pct, isValueUnit) => {
        const safe = clampSliderPct(pct);
        if (safe === 0) return "";
        if (isValueUnit) {
            const max = Number(maxNotional) || 0;
            if (max <= 0) return "";
            const dec = SelectedCoin?.quote_decimal ?? 2;
            const val = (max * safe) / 100;
            return parseFloat(val.toFixed(dec)).toString();
        }
        const max = Number(maxQty) || 0;
        if (max <= 0) return "";
        const qty = toFixed8((max * safe) / 100);
        return qty === 0 ? "" : String(qty);
    };

    const nudgeBuyOrderPrice = (direction) => {
        const tick = Number(SelectedCoin?.tick_size) || 0.01;
        const prec = getPricePrecision();
        const base = buyOrderPrice !== "" ? parseFloat(buyOrderPrice) : Number(buyprice) || 0;
        const n = Number.isFinite(base) ? base : 0;
        const next = Math.round((n + direction * tick) / tick) * tick;
        const clamped = Math.max(next, tick);
        const nextStr = parseFloat(clamped.toFixed(prec)).toString();
        setbuyOrderPrice(nextStr);
        if (infoPlaceOrder === "LIMIT" && limitBuyPercent > 0) {
            setSharedOrderAmount(amountAtMaxPct(maxBuyVolume, maxOpenNotional, limitBuyPercent, classicFuturesValueUnit));
        } else if (limitBuyPercent === 0) {
            setSharedOrderAmount("");
        }
    };

    const applyLimitBuySlider = (pct) => {
        const safe = clampSliderPct(pct);
        setLimitBuyPercent(safe);
        if (safe === 0) {
            setSharedOrderAmount("");
            return;
        }
        setSharedOrderAmount(amountAtMaxPct(maxBuyVolume, maxOpenNotional, safe, classicFuturesValueUnit));
    };

    const applyMarketBuySlider = (pct) => {
        const safe = clampSliderPct(pct);
        setMarketBuyPercent(safe);
        if (safe === 0) {
            setSharedOrderAmount("");
            return;
        }
        setSharedOrderAmount(amountAtMaxPct(maxBuyVolume, maxOpenNotional, safe, classicFuturesValueUnit));
    };

    const applyMarketSellSlider = (pct) => {
        const safe = clampSliderPct(pct);
        setMarketSellPercent(safe);
        if (safe === 0) {
            setSharedOrderAmount("");
            return;
        }
        setSharedOrderAmount(amountAtMaxPct(maxSellVolume, maxOpenNotional, safe, classicFuturesValueUnit));
    };

    const applyLimitSellSlider = (pct) => {
        const safe = clampSliderPct(pct);
        setLimitSellPercent(safe);
        if (safe === 0) {
            setSharedOrderAmount("");
            return;
        }
        setSharedOrderAmount(amountAtMaxPct(maxSellVolume, maxOpenNotional, safe, classicFuturesValueUnit));
    };

    const applyStopBuySlider = (pct) => {
        const safe = clampSliderPct(pct);
        setStopBuyPercent(safe);
        if (safe === 0) {
            setSharedOrderAmount("");
            return;
        }
        setSharedOrderAmount(amountAtMaxPct(maxBuyVolume, maxOpenNotional, safe, classicFuturesValueUnit));
    };

    const applyStopSellSlider = (pct) => {
        const safe = clampSliderPct(pct);
        setStopSellPercent(safe);
        if (safe === 0) {
            setSharedOrderAmount("");
            return;
        }
        setSharedOrderAmount(amountAtMaxPct(maxSellVolume, maxOpenNotional, safe, classicFuturesValueUnit));
    };

    const nudgeSellOrderPrice = (direction) => {
        const tick = Number(SelectedCoin?.tick_size) || 0.01;
        const prec = getPricePrecision();
        const base = sellOrderPrice !== "" ? parseFloat(sellOrderPrice) : Number(sellPrice) || 0;
        const n = Number.isFinite(base) ? base : 0;
        const next = Math.round((n + direction * tick) / tick) * tick;
        const clamped = Math.max(next, tick);
        setsellOrderPrice(parseFloat(clamped.toFixed(prec)).toString());
    };

    const isStopOrder = marginOrderUsesStopTriggerField(infoPlaceOrder);
    const showAmtDenomSelect = marginShowAmtDenomSelect(infoPlaceOrder);
    const isLimitBuyUi = infoPlaceOrder === "LIMIT" && !isStopOrder;
    const showSpotOrderFooter = MARGIN_CLASSIC_SPOT_FORM_KINDS.has(infoPlaceOrder);

    // Re-apply active % slider when contract unit toggles (Amount ↔ Value).
    useEffect(() => {
        if (limitBuyPercent > 0 && infoPlaceOrder === "LIMIT" && !isStopOrder) {
            applyLimitBuySlider(limitBuyPercent);
        }
        if (marketBuyPercent > 0 && infoPlaceOrder === "MARKET") {
            applyMarketBuySlider(marketBuyPercent);
        }
        if (limitSellPercent > 0 && infoPlaceOrder === "LIMIT" && !isStopOrder) {
            applyLimitSellSlider(limitSellPercent);
        }
        if (marketSellPercent > 0 && infoPlaceOrder === "MARKET") {
            applyMarketSellSlider(marketSellPercent);
        }
        if (stopBuyPercent > 0 && isStopOrder) {
            applyStopBuySlider(stopBuyPercent);
        }
        if (stopSellPercent > 0 && isStopOrder) {
            applyStopSellSlider(stopSellPercent);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [futuresContractUnitId]);

    const marginAdvancedFormsBundle = useMemo(
        () => ({
            conditional: { value: marginFormConditional, set: setMarginFormConditional },
            tpSl: { value: marginFormTpSl, set: setMarginFormTpSl },
            twap: { value: marginFormTwap, set: setMarginFormTwap },
            icebergPro: { value: marginFormIcebergPro, set: setMarginFormIcebergPro },
            loopOrder: { value: marginFormLoopOrder, set: setMarginFormLoopOrder },
            trailingStop: { value: marginFormTrailingStop, set: setMarginFormTrailingStop },
            advancedLimit: { value: marginFormAdvancedLimit, set: setMarginFormAdvancedLimit },
        }),
        [
            marginFormConditional,
            marginFormTpSl,
            marginFormTwap,
            marginFormIcebergPro,
            marginFormLoopOrder,
            marginFormTrailingStop,
            marginFormAdvancedLimit,
        ]
    );

    return (
        <>
            <Helmet>
                <title>{`${SelectedCoin?.base_currency || "BTC"}/${SelectedCoin?.quote_currency || "USDT"} Margin Trading – AGCX`}</title>

                <meta
                    name="description"
                    content="Margin trading on AGCX with live market data and the same interface as spot. Register today."
                />

                <meta
                    name="keywords"
                    content="margin trading, bitcoin usdt, AGCX margin, BTC USDT AGCX"
                />
            </Helmet>



            <div className="trade-wrapper spot pb-3 ">
                <div className="  container-fluid">
                    <div className="row g-1 g-md-2" >

                        <TradePairsSection
                            setIsFavouritesOpen={setIsFavouritesOpen}
                            isFavouritesOpen={isFavouritesOpen}
                            token={token}
                            coinFilter={coinFilter}
                            setcoinFilter={setcoinFilter}
                            CoinPairDetails={CoinPairDetails}
                            search={search}
                            setsearch={setsearch}
                            favCoins={favCoins}
                            SelectedCoin={SelectedCoin}
                            handleSelectCoin={handleSelectCoin}
                            coinIconSrc={coinIconSrc}
                            handleAddFav={handleAddFav}
                        />

                        <TradeCenterSection
                            setShowCoinList={setShowCoinList}
                            setShowMobileFavouritesPopup={setShowMobileFavouritesPopup}
                            setIsFavouritesOpen={setIsFavouritesOpen}
                            showCoinList={showCoinList}
                            showTab={showTab}
                            setShowTab={setShowTab}
                            getDescAndLink={getDescAndLink}
                            SelectedCoin={SelectedCoin}
                            coinIconSrc={coinIconSrc}
                            isPricePositive={isPricePositive}
                            displayLastForHeader={displayLastForHeader}
                            priceChange={priceChange}
                            changesHour={changesHour}
                            priceHigh={priceHigh}
                            priceLow={priceLow}
                            volume={volume}
                            desAndLinks={desAndLinks}
                            orderBookActiveTab={orderBookActiveTab}
                            setOrderBookActiveTab={setOrderBookActiveTab}
                            orderBookViewMode={orderBookViewMode}
                            setOrderBookViewMode={setOrderBookViewMode}
                            orderBookAggOpen={orderBookAggOpen}
                            setOrderBookAggOpen={setOrderBookAggOpen}
                            orderBookAggStep={orderBookAggStep}
                            orderBookAggOptions={orderBookAggOptions}
                            setOrderBookAggStep={setOrderBookAggStep}
                            orderBookSwapAmountTotal={orderBookSwapAmountTotal}
                            setOrderBookSwapAmountTotal={setOrderBookSwapAmountTotal}
                            orderBookSocketReady={orderBookSocketReady}
                            displaySellOrders={displaySellOrders}
                            maxSellVolume={maxSellVolume}
                            orderBookMaxSellVolume={orderBookMaxSellVolume}
                            setbuyamount={setbuyamount}
                            infoPlaceOrder={infoPlaceOrder}
                            setbuyOrderPrice={setbuyOrderPrice}
                            formatQuantity={formatQuantity}
                            formatPrice={formatPrice}
                            formatOrderBookAggPrice={formatOrderBookAggPrice}
                            formatOrderBookNotionalShort={formatOrderBookNotionalShort}
                            displayBuyOrders={displayBuyOrders}
                            maxBuyVolume={maxBuyVolume}
                            orderBookMaxBuyVolume={orderBookMaxBuyVolume}
                            maxOpenNotional={maxOpenNotional}
                            setsellAmount={setsellAmount}
                            setsellOrderPrice={setsellOrderPrice}
                            orderBookBidAskRatio={orderBookBidAskRatio}
                            formatPriceThousands={formatPriceThousands}
                            RecentTrade={RecentTrade}
                            fetchSpotWallets={fetchSpotWallets}
                            token={token}
                            walletsLoading={walletsLoading}
                            spotWallets={spotWallets}
                            setShowBuySellTab={setShowBuySellTab}
                            showBuySellTab={showBuySellTab}
                            isConditionalMenuOpen={isConditionalMenuOpen}
                            setIsConditionalMenuOpen={setIsConditionalMenuOpen}
                            applySpotOrderType={applySpotOrderType}
                            orderFieldError={orderFieldError}
                            isSpotDisabled={isSpotDisabled}
                            navigate={navigate}
                            isStopOrder={isStopOrder}
                            buyStopPrice={buyStopPrice}
                            setBuyStopPrice={setBuyStopPrice}
                            buyOrderPrice={buyOrderPrice}
                            nudgeBuyOrderPrice={nudgeBuyOrderPrice}
                            nudgeSellOrderPrice={nudgeSellOrderPrice}
                            priceFieldFocus={priceFieldFocus}
                            setPriceFieldFocus={setPriceFieldFocus}
                            handlePriceInput={handlePriceInput}
                            handlePriceBlur={handlePriceBlur}
                            setLimitPrice={setLimitPrice}
                            applyOrderBookPrice={applyOrderBookPrice}
                            buyamount={buyamount}
                            handleQuantityInput={handleQuantityInput}
                            handleQuantityBlur={handleQuantityBlur}
                            showAmtDenomSelect={showAmtDenomSelect}
                            buyAmountDenom={buyAmountDenom}
                            setbuyAmountDenom={setbuyAmountDenom}
                            futuresContractUnitId={futuresContractUnitId}
                            applyFuturesContractUnit={applyFuturesContractUnit}
                            classicFuturesValueUnit={classicFuturesValueUnit}
                            buyprice={buyprice}
                            isLimitBuyUi={isLimitBuyUi}
                            limitBuyPercent={limitBuyPercent}
                            applyLimitBuySlider={applyLimitBuySlider}
                            marketBuyPercent={marketBuyPercent}
                            applyMarketBuySlider={applyMarketBuySlider}
                            stopBuyPercent={stopBuyPercent}
                            applyStopBuySlider={applyStopBuySlider}
                            sellStopPrice={sellStopPrice}
                            setSellStopPrice={setSellStopPrice}
                            sellOrderPrice={sellOrderPrice}
                            sellAmount={sellAmount}
                            sellAmountDenom={sellAmountDenom}
                            setsellAmountDenom={setsellAmountDenom}
                            sellPrice={sellPrice}
                            limitSellPercent={limitSellPercent}
                            applyLimitSellSlider={applyLimitSellSlider}
                            marketSellPercent={marketSellPercent}
                            applyMarketSellSlider={applyMarketSellSlider}
                            stopSellPercent={stopSellPercent}
                            applyStopSellSlider={applyStopSellSlider}
                            showSpotOrderFooter={showSpotOrderFooter}
                            marginAdvancedFormsBundle={marginAdvancedFormsBundle}
                            advLimitPriceMeta={{
                                tick: Number(SelectedCoin?.tick_size) || 0.01,
                                prec: getPricePrecision(),
                            }}
                            parseOrderQty={parseOrderQty}
                            formatTotal={formatTotal}
                            placeSpotOrder={placeFuturesOrder}
                            placingOrderSide={placingOrderSide}
                            futuresLeverage={futuresLeverage}
                            setFuturesLeverage={setFuturesLeverage}
                            futuresReduceOnly={futuresReduceOnly}
                            setFuturesReduceOnly={setFuturesReduceOnly}
                            KycStatus={KycStatus}
                            BuyCoinBal={BuyCoinBal}
                            SellCoinBal={SellCoinBal}
                            limitBuyIoc={limitBuyIoc}
                            setLimitBuyIoc={setLimitBuyIoc}
                            limitBuyFok={limitBuyFok}
                            setLimitBuyFok={setLimitBuyFok}
                            limitSellIoc={limitSellIoc}
                            setLimitSellIoc={setLimitSellIoc}
                            limitSellFok={limitSellFok}
                            setLimitSellFok={setLimitSellFok}
                            OrderBookHeaderRow={OrderBookHeaderRow}
                            OrderBookAskRow={OrderBookAskRow}
                            OrderBookBidRow={OrderBookBidRow}
                            OrderBookMidPriceRow={OrderBookMidPriceRow}
                            OrderBookBidAskFooter={OrderBookBidAskFooter}
                            buyOrderAfterTrigger={buyOrderAfterTrigger}
                            setBuyOrderAfterTrigger={setBuyOrderAfterTrigger}
                            sellOrderAfterTrigger={sellOrderAfterTrigger}
                            setSellOrderAfterTrigger={setSellOrderAfterTrigger}
                            futuresPositions={futuresPositions}
                            futuresOpenOrders={futuresOpenOrders}
                        />
                        <TradeHistorySection
                            positionOrderTab={positionOrderTab}
                            setPositionOrderTab={setPositionOrderTab}
                            SPOT_OPEN_ORDER_KINDS={SPOT_OPEN_ORDER_KINDS}
                            openOrderKindTab={openOrderKindTab}
                            setOpenOrderKindTab={setOpenOrderKindTab}
                            orderType={orderType}
                            setorderType={setorderType}
                            openOrders={openOrders}
                            matchesOpenOrderKind={matchesOpenOrderKind}
                            getOpenOrderPair={getOpenOrderPair}
                            formatSpotOpenOrderDateTime={formatSpotOpenOrderDateTime}
                            spotOpenOrderTypeLabel={spotOpenOrderTypeLabel}
                            spotOpenOrderTifLabel={spotOpenOrderTifLabel}
                            safeToFixed8={safeToFixed8}
                            getOpenOrderStopPrice={getOpenOrderStopPrice}
                            nineDecimalFormat={nineDecimalFormat}
                            spotOpenOrderFilledQty={spotOpenOrderFilledQty}
                            spotOpenOrderRemainingQty={spotOpenOrderRemainingQty}
                            cancelOrder={cancelOrder}
                            tradeHistorySideFilter={tradeHistorySideFilter}
                            setTradeHistorySideFilter={setTradeHistorySideFilter}
                            myTrades={spotTradeHistoryRows}
                            spotHistoryViewportKey={selectedPairApiCompact}
                            onSpotOrderHistoryScrollViewport={handleSpotOrderHistoryScroll}
                            onSpotTradeHistoryScrollViewport={handleSpotTradeHistoryScroll}
                            tradeHistoryBaseAsset={tradeHistoryBaseAsset}
                            tradeHistoryQuoteAsset={tradeHistoryQuoteAsset}
                            formatTradeHistoryDateTime={formatTradeHistoryDateTime}
                            tradeHistoryMarketLabel={tradeHistoryMarketLabel}
                            tradeHistoryRoleLabel={tradeHistoryRoleLabel}
                            pastOrderType={pastOrderType}
                            setpastOrderType={setpastOrderType}
                            pastOrders={pastOrders}
                            orderHistoryQuoteCurrency={orderHistoryQuoteCurrency}
                            orderHistoryMarketLabel={orderHistoryMarketLabel}
                            orderHistoryExecutedValueNum={orderHistoryExecutedValueNum}
                            orderHistoryStatusLabel={orderHistoryStatusLabel}
                            orderHistoryStatusClass={orderHistoryStatusClass}
                            spotOrderLifecycleStatus={spotOrderLifecycleStatus}
                            expandedRowIndex={expandedRowIndex}
                            setExpandedRowIndex={setExpandedRowIndex}
                            formatOrderHistoryDateTime={formatOrderHistoryDateTime}
                            showExecutedTrades={showExecutedTrades}
                            setShowExecutedTrades={setShowExecutedTrades}
                            fetchSpotWallets={fetchSpotWallets}
                            SelectedCoin={SelectedCoin}
                            token={token}
                            BuyCoinBal={BuyCoinBal}
                            SellCoinBal={SellCoinBal}
                            displayLastForHeader={displayLastForHeader}
                            formatPriceThousands={formatPriceThousands}
                            volume={volume}
                            buyprice={buyprice}
                            sellPrice={sellPrice}
                            futuresOpenOrders={futuresOpenOrders}
                            futuresOrderHistory={futuresOrderHistory}
                            futuresPositions={futuresPositions}
                            futuresPositionHistory={futuresPositionHistory}
                            futuresTradeHistory={futuresTradeHistory}
                            futuresWalletHistory={futuresWalletHistory}
                            futuresWalletHistoryPagination={futuresWalletHistoryPagination}
                            walletHistoryFilters={walletHistoryFilters}
                            setWalletHistoryFilters={setWalletHistoryFilters}
                            cancelFuturesOrder={cancelFuturesOrder}
                            closeFuturesPosition={closeFuturesPosition}
                            futuresContracts={AllData?.pairs ?? []}
                            futuresAccounts={futuresAccounts}
                            futuresAccountPositions={futuresAccountPositions}
                        />

                    </div>
                </div>
            </div >

            <FuturesRiskWarningModal
                open={futuresRiskWarningOpen}
                onAcknowledge={acknowledgeFuturesRiskWarning}
            />

            <FuturesOrderRejectionModal
                open={futuresOrderRejectionOpen}
                onClose={() => setFuturesOrderRejectionOpen(false)}
                message={FUTURES_LEVERAGE_REJECTION_MSG}
            />

            {/* Mobile Favourites Popup */}
            {showMobileFavouritesPopup && (
                <div className="mobile-favourites-popup-overlay" onClick={() => setShowMobileFavouritesPopup(false)}>
                    <div className="mobile-favourites-popup pairpopup" onClick={(e) => e.stopPropagation()}>
                        <div className="mobile-favourites-popup-header">
                            <h4>Select Pair</h4>
                            <button className="mobile-favourites-close-btn" onClick={() => setShowMobileFavouritesPopup(false)}>
                                <i className="ri-close-line"></i>
                            </button>
                        </div>
                        <div className="mobile-favourites-popup-content">
                            <div className="spotLists">
                                {/* Search */}
                                <div className="spot-list-search">
                                    <div className="ivu-input">
                                        <i className="ri-search-2-line"></i>
                                        <input
                                            autoComplete="off"
                                            spellCheck="false"
                                            type="search"
                                            placeholder="Search"
                                            onChange={(e) => setsearch(e.target.value)}
                                            value={search}
                                        />
                                    </div>
                                </div>

                                <ul className="favorites_list_tabs">
                                    {token && (
                                        <li>
                                            <button
                                                className={coinFilter === 'FAV' ? 'active' : ''}
                                                onClick={() => setcoinFilter('FAV')}
                                            >
                                                Favourites
                                            </button>
                                        </li>
                                    )}
                                    {CoinPairDetails && [...new Set(CoinPairDetails.map(item => item?.quote_currency)), "BTC", "BNB", "ETH"].map((quoteCurrency, idx) => (
                                        <li key={idx}>
                                            <button
                                                className={coinFilter === quoteCurrency ? 'active' : ''}
                                                onClick={() => setcoinFilter(quoteCurrency)}
                                            >
                                                {quoteCurrency}
                                            </button>
                                        </li>
                                    ))}
                                </ul>

                                {/* Table */}
                                <div className="price_card table-responsive">
                                    <table className="table table-sm table-borderless mb-0 orderbook-table">
                                        <thead>
                                            <tr>
                                                <th>Pair</th>
                                                <th className="text-end">Price</th>
                                                <th className="text-end">Change</th>
                                            </tr>
                                        </thead>
                                        <tbody className="price_card_body">
                                            {CoinPairDetails &&
                                                CoinPairDetails.map((data, index) => {
                                                    // Filter by favorites
                                                    if (coinFilter === "FAV" && !favCoins.includes(data?._id)) {
                                                        return null;
                                                    }
                                                    // Filter by quote currency
                                                    if (coinFilter !== "FAV" && (data?.quote_currency !== coinFilter && data?.base_currency !== coinFilter)) {
                                                        return null;
                                                    }

                                                    const isActive =
                                                        SelectedCoin?.base_currency === data?.base_currency &&
                                                        SelectedCoin?.quote_currency === data?.quote_currency;

                                                    return (
                                                        <tr
                                                            key={index}
                                                            className={isActive ? "active" : ""}
                                                            style={{ cursor: "pointer" }}
                                                            onClick={() => {
                                                                handleSelectCoin(data);
                                                                setShowMobileFavouritesPopup(false);
                                                            }}
                                                        >
                                                            {/* Pair */}
                                                            <td>
                                                                <div className="d-flex align-items-center gap-1">
                                                                    <img
                                                                        src={coinIconSrc(data?.icon_path)}
                                                                        alt=""
                                                                        className="img-fluid me-1 round_img"
                                                                    />
                                                                    <div className="d-flex flex-column pair-color">
                                                                        {`${data?.base_currency}/${data?.quote_currency}`}
                                                                        <span className="tokensubcnt">{data?.base_currency_fullname}</span>
                                                                    </div>
                                                                </div>
                                                            </td>

                                                            {/* Price */}
                                                            <td className="text-end">
                                                                <div className="d-flex flex-column pair-color">
                                                                    <span>{formatContractPrice(data?.buy_price, data?.tick_size)}</span>
                                                                    <span className="tokensubcnt">${formatContractPrice(data?.buy_price, data?.tick_size)}</span>
                                                                </div>
                                                            </td>

                                                            {/* Change + Star */}
                                                            <td className="text-end">
                                                                <div className="d-flex justify-content-end align-items-center gap-2">
                                                                    <div className="d-flex flex-column pair-color text-end">
                                                                        <span
                                                                            className={
                                                                                data?.change_percentage >= 0
                                                                                    ? "text-green"
                                                                                    : "text-danger"
                                                                            }
                                                                        >
                                                                            {data?.change_percentage >= 0 ? `+${Number(parseFloat(data?.change_percentage)?.toFixed(5))}` : Number(parseFloat(data?.change_percentage)?.toFixed(5))}%
                                                                        </span>
                                                                        <span className="tokensubcnt">{parseFloat(data?.change) || 0}</span>
                                                                    </div>

                                                                    {token && (
                                                                        <i
                                                                            className={
                                                                                favCoins.includes(data?._id)
                                                                                    ? "ri ri-star-fill ri-xl"
                                                                                    : "ri ri-star-line ri-xl"
                                                                            }
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                handleAddFav(data?._id);
                                                                            }}
                                                                        />
                                                                    )}
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    )
}

function FuturesTradePageRoot() {
    return (
        <FuturesTradeTransferProvider>
            <Margin />
        </FuturesTradeTransferProvider>
    );
}

export default FuturesTradePageRoot;
